import React, { useState } from 'react';
import { 
  Copy, 
  Check, 
  Send, 
  Truck, 
  Package, 
  FileCode2, 
  Sparkles, 
  Webhook, 
  ShieldCheck, 
  Layers,
  Cpu,
  AlertCircle,
  Info
} from 'lucide-react';
import { showToast } from '../lib/toast';

export default function DeveloperDocs() {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'three-products' | 'laravel' | 'webhook' | 'overview'>('three-products');
  const [productSubTab, setProductSubTab] = useState<'digital' | 'physical_advance' | 'physical_full_payment' | 'physical_cod'>('digital');
  const [frameworkTab, setFrameworkTab] = useState<'laravel' | 'php' | 'nodejs' | 'python'>('laravel');
  const [webhookFrameworkTab, setWebhookFrameworkTab] = useState<'laravel' | 'php' | 'nodejs' | 'python'>('laravel');

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    showToast('Code copied to clipboard!', 'success');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const getWebhookCode = () => {
    switch (webhookFrameworkTab) {
      case 'laravel':
        return laravelWebhookCode;
      case 'php':
        return rawPhpWebhookCode;
      case 'nodejs':
        return nodeJsWebhookCode;
      case 'python':
        return pythonWebhookCode;
      default:
        return laravelWebhookCode;
    }
  };

  const getWebhookFileName = () => {
    switch (webhookFrameworkTab) {
      case 'laravel':
        return 'app/Http/Controllers/MecoPayWebhookController.php';
      case 'php':
        return 'webhook.php (Core PHP)';
      case 'nodejs':
        return 'controllers/webhookController.js (Express)';
      case 'python':
        return 'views_webhook.py (Django / Flask)';
      default:
        return 'MecoPayWebhookController.php';
    }
  };

  const getFrameworkCode = () => {
    switch (frameworkTab) {
      case 'laravel':
        return laravelFullController;
      case 'php':
        return rawPhpController;
      case 'nodejs':
        return nodeJsFullController;
      case 'python':
        return pythonFullController;
      default:
        return laravelFullController;
    }
  };

  const getFrameworkFileName = () => {
    switch (frameworkTab) {
      case 'laravel':
        return 'app/Http/Controllers/PaymentController.php';
      case 'php':
        return 'process_payment.php (Core PHP)';
      case 'nodejs':
        return 'controllers/paymentController.js (Express)';
      case 'python':
        return 'views.py / payment.py (Django / Flask)';
      default:
        return 'PaymentController.php';
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 text-slate-200">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-emerald-950/60 via-slate-900/80 to-slate-900 border border-emerald-500/30 rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-xl">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/20">
            <Sparkles className="w-3.5 h-3.5" /> Official API Release v2.5 • Zero-Waste Engine
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            MecoPay Multi-Product & Developer Docs
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-xl leading-relaxed">
            Laravel, Raw PHP, Node.js, Python এবং কাস্টম ওয়েবসাইটের জন্য Digital, Physical Advance ও 100% Full COD ইন্টিগ্রেশন স্পেসিফিকেশন।
          </p>
        </div>
      </div>

      {/* Cart Integrity & Prohibited Mixed Cart Banner */}
      <div className="bg-slate-900/80 border-2 border-amber-500/30 rounded-3xl p-6 space-y-4 shadow-xl">
        <div className="flex items-center gap-2.5 text-amber-400 font-bold text-base">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
          <span>প্রোডাক্ট অর্ডার ও কার্ট পলিসি (Cart Integrity & Prohibited Rules):</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-2xl bg-slate-950 border border-amber-500/20 space-y-2">
            <div className="text-xs font-bold text-amber-300 flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-[10px]">১</span>
              একই অর্ডারে সর্বোচ্চ ১টি ডিজিটাল প্রোডাক্ট
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              স্বয়ংক্রিয় পাসকোড, টেলিগ্রাম ইনভাইট ও ড্রাইভ লিংকের পূর্ণ নিরাপত্তা নিশ্চিত করতে গ্রাহক একবারে সর্বোচ্চ ১টি ডিজিটাল প্রোডাক্ট কিনতে পারবেন।
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-950 border border-emerald-500/20 space-y-2">
            <div className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-[10px]">২</span>
              একাধিক ফিজিক্যাল প্রোডাক্ট কার্ট অনুমোদন (Allowed)
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              গ্রাহক চাইলে কার্টে যত ইচ্ছা একাধিক ফিজিক্যাল প্রোডাক্ট একসাথে যুক্ত করে অর্ডার করতে পারবেন। কার্টের মোট পণ্যের মূল্য ও কুরিয়ার ফি একত্রিত করে অ্যাডভান্স বা COD প্রসেস হবে।
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-950 border border-red-500/20 space-y-2">
            <div className="text-xs font-bold text-red-400 flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-[10px]">৩</span>
              মিক্সড কার্ট সম্পূর্ণ নিষিদ্ধ (Mixed Cart Prohibited)
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
              ডিজিটাল এবং ফিজিক্যাল প্রোডাক্ট একই সাথে এক অর্ডারে কেনা যাবে না। কারণ ডিজিটাল পণ্যে তাৎক্ষণিক ১০০% পেমেন্ট লাগে, আর ফিজিক্যাল পণ্যে কুরিয়ার ও অ্যাডভান্স হ্যান্ডলিং প্রয়োজন।
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-px overflow-x-auto">
        <button
          onClick={() => setActiveTab('three-products')}
          className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
            activeTab === 'three-products'
              ? 'border-emerald-500 text-emerald-400 bg-slate-900'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Layers className="w-4 h-4" /> ১. ৪ ধরনের প্রোডাক্ট ও JSON
        </button>

        <button
          onClick={() => setActiveTab('laravel')}
          className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
            activeTab === 'laravel'
              ? 'border-emerald-500 text-emerald-400 bg-slate-900'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <FileCode2 className="w-4 h-4" /> ২. Backend & Custom Code (PHP/JS/Python)
        </button>

        <button
          onClick={() => setActiveTab('webhook')}
          className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
            activeTab === 'webhook'
              ? 'border-emerald-500 text-emerald-400 bg-slate-900'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Webhook className="w-4 h-4" /> ৩. Webhook & রিয়েল-টাইম সিঙ্ক
        </button>

        

        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2.5 text-xs font-bold rounded-t-xl transition-all flex items-center gap-2 border-b-2 whitespace-nowrap ${
            activeTab === 'overview'
              ? 'border-emerald-500 text-emerald-400 bg-slate-900'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <ShieldCheck className="w-4 h-4" /> ৪. API Credentials & Base URL
        </button>
      </div>

      {/* TAB 1: THREE PRODUCT TYPES & JSON */}
      {activeTab === 'three-products' && (
        <div className="space-y-6">
          {/* Sub Tab Switcher */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <button
              onClick={() => setProductSubTab('digital')}
              className={`p-4 rounded-2xl border text-left transition-all ${
                productSubTab === 'digital'
                  ? 'bg-blue-950/40 border-blue-500 text-white shadow-lg shadow-blue-500/10'
                  : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-2 font-bold text-xs text-blue-400 mb-1">
                <Send className="w-4 h-4" /> ১. Digital Product (100% Pay)
              </div>
              <div className="text-[11px] text-slate-300 leading-snug">
                ১০০% পেমেন্ট + সাকসেস URL আবশ্যক + Telegram/FB/Drive লিংক ও পাসকোড ডেলিভারি।
              </div>
            </button>

            <button
              onClick={() => setProductSubTab('physical_advance')}
              className={`p-4 rounded-2xl border text-left transition-all ${
                productSubTab === 'physical_advance'
                  ? 'bg-amber-950/40 border-amber-500 text-white shadow-lg shadow-amber-500/10'
                  : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-2 font-bold text-xs text-amber-400 mb-1">
                <Truck className="w-4 h-4" /> ২. Physical (Advance + Courier Fee)
              </div>
              <div className="text-[11px] text-slate-300 leading-snug">
                মোট মূল্য + কুরিয়ার চার্জ থেকে ১৫০৳ অগ্রিম চার্জ এবং বাকি টাকা কুরিয়ারে ডিউ কালেকশন।
              </div>
            </button>

            <button
              onClick={() => setProductSubTab('physical_full_payment')}
              className={`p-4 rounded-2xl border text-left transition-all ${
                productSubTab === 'physical_full_payment'
                  ? 'bg-cyan-950/40 border-cyan-500 text-white shadow-lg shadow-cyan-500/10'
                  : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-2 font-bold text-xs text-cyan-400 mb-1">
                <Package className="w-4 h-4" /> ৩. Physical Full Payment
              </div>
              <div className="text-[11px] text-slate-300 leading-snug">
                পণ্য ও কুরিয়ার চার্জের সম্পূর্ণ টাকা অনলাইনে; courier due COD = ০৳।
              </div>
            </button>

            <button
              onClick={() => setProductSubTab('physical_cod')}
              className={`p-4 rounded-2xl border text-left transition-all ${
                productSubTab === 'physical_cod'
                  ? 'bg-emerald-950/40 border-emerald-500 text-white shadow-lg shadow-emerald-500/10'
                  : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-2 font-bold text-xs text-emerald-400 mb-1">
                <Package className="w-4 h-4" /> ৪. Physical (100% COD + Courier Fee)
              </div>
              <div className="text-[11px] text-slate-300 leading-snug">
                গেটওয়েতে ০৳ চার্জ। পণ্যমূল্য + কুরিয়ার ফি সহ মোট টাকা ডেলিভারিতে আদায় ও কুরিয়ার বুকিং।
              </div>
            </button>
          </div>

          {/* Sub Tab Content 1: Digital */}
          {productSubTab === 'digital' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-semibold border border-blue-500/20 mb-2">
                    <Send className="w-3.5 h-3.5" /> 100% Digital Payment Payload
                  </div>
                  <h3 className="text-lg font-bold text-white">Digital Product JSON Payload Specification</h3>
                  <p className="text-xs text-slate-400">
                    ফুল পেমেন্ট সফল হওয়ার সাথে সাথে গ্রাহককে <code>success_url</code>-এ ফিরিয়ে আনা হয় এবং স্ক্রিনে পাসকোড দেওয়া হয়।
                  </p>
                </div>
                <button
                  onClick={() => handleCopy(digitalJsonPayload, 1)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold rounded-xl text-xs flex items-center gap-1.5 transition-all border border-slate-700 shrink-0 self-start sm:self-auto"
                >
                  {copiedIndex === 1 ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} Copy JSON
                </button>
              </div>

              <pre className="bg-slate-950 p-4 sm:p-5 rounded-2xl border border-slate-800 text-xs font-mono text-blue-300 overflow-x-auto leading-relaxed">
{digitalJsonPayload}
              </pre>

              {/* Mandatory vs Optional Table for Digital */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse border border-slate-800 rounded-xl overflow-hidden">
                  <thead>
                    <tr className="bg-slate-950 text-slate-300 font-semibold border-b border-slate-800">
                      <th className="p-3">ফিল্ড নেম</th>
                      <th className="p-3">স্ট্যাটাস</th>
                      <th className="p-3">বিবরণ ও সার্ভিস গাইডলাইন</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 text-slate-400">
                    <tr>
                      <td className="p-3 font-mono text-blue-300">order_id</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">ক্লায়েন্টের unique order ID; এটি বাধ্যতামূলক।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">product_name</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">শুধু invoice বা checkout-এ display করার জন্য; order matching-এর জন্য ব্যবহার হবে না।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">product_type</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">মান হতে হবে <code>"digital"</code>।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">currency</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">মুদ্রার কোড (যেমন: <code>"USD"</code>, <code>"BDT"</code>, <code>"EUR"</code>, <code>"GBP"</code>, <code>"USDT"</code>)। <strong>বাধ্যতামূলক:</strong> যেহেতু দেশ ও গেটওয়েভেদে একই সংখ্যার মূল্যের বিশাল পার্থক্য (যেমন ১০০ USD এবং ১০০ BDT এক নয়), তাই সঠিক কারেন্সি কোড পাঠানো বাধ্যতামূলক। পে-লোডে না পাঠালে সিস্টেম স্বয়ংক্রিয়ভাবে ব্র্যান্ড/গেটওয়ের ডিফল্ট কারেন্সি ফ্রন্টএন্ডে প্রদর্শন করবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">amount</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">ডিজিটাল পণ্যের ১০০% সম্পূর্ণ মূল্য।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">success_url</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">পেমেন্ট সফল হলে আপনার ওয়েবসাইটের যে পেজে কাস্টমারকে রিডাইরেক্ট করা হবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">telegram_link / facebook_group_link / google_drive_link</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">১টি, ২টি, ৩টি লিংক পাঠাতে পারেন অথবা কোনো লিংক না পাঠালেও চলবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">customer_phone</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 text-[10px] font-semibold">ঐচ্ছিক (সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">গ্রাহকের মোবাইল নম্বর। আপনার ব্র্যান্ডে যদি <strong>SMS বা WhatsApp নোটিফিকেশন সার্ভিস অন</strong> থাকে, তবে অর্ডার সফল হওয়ার পর ফোনে নিশ্চিতকরণ মেসেজ ও পাসকোড পাঠাতে এই নম্বর পাঠানো <strong>আবশ্যক</strong> (অন্যথায় কোন নম্বরে সাকসেস মেসেজ যাবে?)। সার্ভিস সক্রিয় না থাকলে না পাঠালেও কোনো সমস্যা নেই।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">customer_email</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 text-[10px] font-semibold">ঐচ্ছিক (সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">গ্রাহকের ইমেইল। ব্র্যান্ডে যদি <strong>Email সার্ভিস অন</strong> থাকে, তবে ডিজিটাল এক্সেস লিঙ্ক ও ডিজিটাল ইনভয়েস গ্রাহকের ইনবক্সে পৌঁছাতে ইমেইল পাঠানো <strong>আবশ্যক</strong>। SMS ও Email উভয় সার্ভিস অন থাকলে দুটি ফিল্ডই পাঠাতে হবে। কোনো সার্ভিস চালু না থাকলে না পাঠালেও চলবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-blue-300">customer_name</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (সুপারিশকৃত)</span></td>
                      <td className="p-3 text-slate-300">গ্রাহকের নাম (ড্যাশবোর্ড ইনভয়েস ও মেসেজে ব্যক্তিগতকরণের জন্য সুপারিশকৃত)।</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Service Policy Notice Box for Digital */}
              <div className="bg-slate-950/80 border border-slate-800/80 rounded-2xl p-4 text-xs space-y-2">
                <div className="flex items-center gap-2 font-bold text-blue-400">
                  <Info className="w-4 h-4 shrink-0" />
                  <span>সার্ভিস ইন্টিগ্রেশন নোট (Digital Products):</span>
                </div>
                <p className="text-slate-400 leading-relaxed text-[11px]">
                  ডিজিটাল পণ্যের ক্ষেত্রে ক্লায়েন্টের ওয়েবসাইটে গ্রাহক ইতিমধ্যে লগইন বা রেজিস্টার্ড অবস্থায় থাকতে পারেন। MecoPay কোর গেটওয়েতে নাম, ফোন বা ইমেইল পাঠানো বাধ্যতামূলক নয়। তবে আপনার ব্র্যান্ডে যদি <strong>SMS / WhatsApp / Email সার্ভিস</strong> সক্রিয় করা থাকে, তবে সেই সার্ভিসগুলোর মাধ্যমে সফল ডেলিভারি নিশ্চিত করতে সংশ্লিষ্ট ফিল্ডগুলো পাঠাতে হবে। কোনো সার্ভিস সক্রিয় না থাকলে এগুলো ছাড়া শুধু <code>order_id</code>, <code>amount</code> ও <code>success_url</code> পাঠালেই চলবে।
                </p>
              </div>
            </div>
          )}

          {/* Sub Tab Content 2: Physical Advance */}
          {productSubTab === 'physical_advance' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 text-xs font-semibold border border-amber-500/20 mb-2">
                    <Truck className="w-3.5 h-3.5" /> Advance Payment + Courier Delivery Charge
                  </div>
                  <h3 className="text-lg font-bold text-white">Physical Product (Advance + COD) JSON Payload</h3>
                  <p className="text-xs text-slate-400">
                    পণ্যের মূল্য + কুরিয়ার চার্জ থেকে অগ্রিম ফি চার্জ হবে এবং অবশিষ্ট টাকা কুরিয়ারে ডিউ থাকবে।
                  </p>
                </div>
                <button
                  onClick={() => handleCopy(advanceJsonPayload, 2)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold rounded-xl text-xs flex items-center gap-1.5 transition-all border border-slate-700 shrink-0 self-start sm:self-auto"
                >
                  {copiedIndex === 2 ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} Copy JSON
                </button>
              </div>

              <pre className="bg-slate-950 p-4 sm:p-5 rounded-2xl border border-slate-800 text-xs font-mono text-amber-300 overflow-x-auto leading-relaxed">
{advanceJsonPayload}
              </pre>

              {/* Mandatory vs Optional Table for Physical Advance */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse border border-slate-800 rounded-xl overflow-hidden">
                  <thead>
                    <tr className="bg-slate-950 text-slate-300 font-semibold border-b border-slate-800">
                      <th className="p-3">ফিল্ড নেম</th>
                      <th className="p-3">স্ট্যাটাস</th>
                      <th className="p-3">বিবরণ ও সার্ভিস গাইডলাইন</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 text-slate-400">
                    <tr>
                      <td className="p-3 font-mono text-amber-300">order_id</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">ক্লায়েন্টের unique order ID; এটি বাধ্যতামূলক।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">product_name</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">শুধু invoice বা checkout-এ display করার জন্য; order matching-এর জন্য ব্যবহার হবে না।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">product_type</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">মান হতে হবে <code>"physical_advance"</code>।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">currency</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">মুদ্রার কোড (যেমন: <code>"USD"</code>, <code>"BDT"</code>, <code>"EUR"</code>, <code>"GBP"</code>)। <strong>বাধ্যতামূলক:</strong> বিভিন্ন দেশে ও গেটওয়েতে মুদ্রার মানের ভিন্নতার কারণে কারেন্সি উল্লেখ করা বাধ্যতামূলক। পে-লোডে না পেলে ব্র্যান্ড/গেটওয়ে কনফিগ থেকে ফ্রন্টএন্ডে কারেন্সি প্রদর্শিত হবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">product_price</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">পণ্যের মূল মূল্য (যেমন: ১২৫০ ৳)।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">courier_charge</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">ডেলিভারি ফি (যেমন: ১২০ ৳)।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">advance_amount</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">অনলাইন গেটওয়েতে কাস্টমার থেকে তাৎক্ষণিক advance charge (যেমন: ১৫০ ৳)। Total-এর সমান বা বেশি হলে due COD শূন্য হবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">success_url</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">পেমেন্ট সফল হলে আপনার ওয়েবসাইটের অর্ডার সাকসেস পেজে রিডাইরেক্ট করার URL।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">customer_address</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 text-[10px] font-semibold">ঐচ্ছিক (কুরিয়ার সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">গ্রাহকের সম্পূর্ণ ডেলিভারি ঠিকানা। আপনি যদি MecoPay ড্যাশবোর্ডে <strong>অটো কুরিয়ার বুকিং (Auto Courier / 1-Click Booking)</strong> সার্ভিস অন রাখেন, তবে কুরিয়ার সিস্টেমে পার্সেল বুক করার জন্য গ্রাহকের সম্পূর্ণ ঠিকানা পাঠানো <strong>আবশ্যক</strong>। অটো কুরিয়ার সার্ভিস ব্যবহার না করলে এটি না পাঠালেও চলবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">customer_phone</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 text-[10px] font-semibold">ঐচ্ছিক (সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">কুরিয়ার ডেলিভারিম্যানের সাথে যোগাযোগ এবং SMS/WhatsApp নোটিফিকেশন সার্ভিস অন থাকলে মোবাইল নম্বর পাঠানো <strong>আবশ্যক</strong>। সার্ভিস চালু না থাকলে না পাঠালেও কোনো সমস্যা নেই।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">customer_email</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 text-[10px] font-semibold">ঐচ্ছিক (সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">গ্রাহককে ডিজিটাল ইনভয়েস ও ট্র্যাকিং রসিদ পাঠাতে ইমেইল সার্ভিস অন থাকলে গ্রাহকের ইমেইল পাঠানো <strong>আবশ্যক</strong>।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-amber-300">customer_name</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">পার্সেল রিসিভারের নাম (কুরিয়ার শিপমেন্ট লেবেল ও ইনভয়েসের জন্য)।</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Service Policy Notice Box for Physical Advance */}
              <div className="bg-slate-950/80 border border-slate-800/80 rounded-2xl p-4 text-xs space-y-2">
                <div className="flex items-center gap-2 font-bold text-amber-400">
                  <Info className="w-4 h-4 shrink-0" />
                  <span>সার্ভিস ইন্টিগ্রেশন নোট (Advance + COD):</span>
                </div>
                <p className="text-slate-400 leading-relaxed text-[11px]">
                  ক্লায়েন্টের ওয়েবসাইটে চেকআউটের সময় কাস্টমার ইতোমধ্যে তার নাম, ফোন ও ঠিকানা পূরণ করে থাকেন। আপনার ব্র্যান্ডে যদি <strong>অটো কুরিয়ার বুকিং</strong> চালু থাকে, তবে কুরিয়ার বুকিং সফল করতে <code>customer_address</code> এবং <code>customer_phone</code> পাঠানো আবশ্যক। একইভাবে <strong>SMS বা Email নোটিফিকেশন</strong> চালু থাকলে স্বয়ংক্রিয় মেসেজ প্রেরণের জন্য ফোন বা ইমেইল পাঠাবেন। সার্ভিসগুলো বন্ধ থাকলে ফিল্ডগুলো না পাঠালেও সিস্টেম স্বাভাবিকভাবে কাজ করবে।
                </p>
              </div>
            </div>
          )}

          {/* Sub Tab Content 3: Physical Full Payment */}
          {productSubTab === 'physical_full_payment' && (
            <div className="bg-slate-900/60 border border-cyan-500/30 rounded-3xl p-6 sm:p-8 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-semibold border border-cyan-500/20 mb-2">
                    <Package className="w-3.5 h-3.5" /> Physical Full Payment
                  </div>
                  <h3 className="text-lg font-bold text-white">Physical Product (Full Payment) JSON Payload</h3>
                  <p className="text-xs text-slate-400">পণ্যের মূল্য ও courier charge সম্পূর্ণ online payment হবে; API Worker-এর হিসাব অনুযায়ী <code>cod_due = 0</code>।</p>
                </div>
                <button onClick={() => handleCopy(fullPaymentJsonPayload, 4)} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold rounded-xl text-xs border border-slate-700">{copiedIndex === 4 ? 'Copied' : 'Copy JSON'}</button>
              </div>
              <pre className="bg-slate-950 p-4 sm:p-5 rounded-2xl border border-slate-800 text-xs font-mono text-cyan-300 overflow-x-auto leading-relaxed">{fullPaymentJsonPayload}</pre>

              {/* Mandatory vs Optional Table for Physical Full Payment */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse border border-slate-800 rounded-xl overflow-hidden">
                  <thead>
                    <tr className="bg-slate-950 text-slate-300 font-semibold border-b border-slate-800">
                      <th className="p-3">ফিল্ড নেম</th>
                      <th className="p-3">স্ট্যাটাস</th>
                      <th className="p-3">বিবরণ ও সার্ভিস গাইডলাইন</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 text-slate-400">
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">order_id</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">ক্লায়েন্টের unique order ID; পরবর্তী webhook matching-এর জন্য প্রয়োজনীয়।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">product_name</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">Invoice বা checkout display-এর জন্য; না দিলে Worker default name ব্যবহার করবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">product_type</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">মান হতে হবে <code>"physical_full_payment"</code>।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">currency</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">অর্ডারের মুদ্রার কোড, যেমন <code>"BDT"</code>, <code>"USD"</code> বা <code>"EUR"</code>।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">product_price</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">পণ্যের মূল্য; Full Payment-এ সম্পূর্ণ online payment-এর হিসাবের অংশ।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">courier_charge</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">ডেলিভারি চার্জ; Free delivery হলে <code>0</code> পাঠাবেন।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">success_url</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">পেমেন্ট সফল হলে client website-এর order success page-এ ফেরত পাঠানোর URL; production integration-এ পাঠানো recommended।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">cancel_url</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">পেমেন্ট বাতিল হলে client website-এ ফেরত পাঠানোর URL।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">customer_name / customer_phone / customer_address</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 text-[10px] font-semibold">ঐচ্ছিক (কুরিয়ার সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">Auto courier booking চালু থাকলে parcel booking-এর জন্য customer-এর নাম, ফোন ও পূর্ণ delivery address পাঠানো প্রয়োজন।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-cyan-300">customer_email</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 text-[10px] font-semibold">ঐচ্ছিক (সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">Email invoice বা receipt service চালু থাকলে customer-এর email পাঠাবেন।</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub Tab Content 4: Physical COD */}
          {productSubTab === 'physical_cod' && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/20 mb-2">
                    <Package className="w-3.5 h-3.5" /> 100% Cash on Delivery (Just Form)
                  </div>
                  <h3 className="text-lg font-bold text-white">Physical (100% COD / Just Form) JSON Payload</h3>
                  <p className="text-xs text-slate-400">
                    গেটওয়েতে কোনো পেমেন্ট ছাড়া (amount = 0) পণ্যমূল্য ও কুরিয়ার চার্জ সহ পুরো টাকা কুরিয়ারে ডিউ হবে।
                  </p>
                </div>
                <button
                  onClick={() => handleCopy(codJsonPayload, 3)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold rounded-xl text-xs flex items-center gap-1.5 transition-all border border-slate-700 shrink-0 self-start sm:self-auto"
                >
                  {copiedIndex === 3 ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} Copy JSON
                </button>
              </div>

              <pre className="bg-slate-950 p-4 sm:p-5 rounded-2xl border border-slate-800 text-xs font-mono text-emerald-300 overflow-x-auto leading-relaxed">
{codJsonPayload}
              </pre>

              {/* Mandatory vs Optional Table for Full COD */}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse border border-slate-800 rounded-xl overflow-hidden">
                  <thead>
                    <tr className="bg-slate-950 text-slate-300 font-semibold border-b border-slate-800">
                      <th className="p-3">ফিল্ড নেম</th>
                      <th className="p-3">স্ট্যাটাস</th>
                      <th className="p-3">বিবরণ ও সার্ভিস গাইডলাইন</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 text-slate-400">
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">order_id</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">ক্লায়েন্টের unique order ID; এটি বাধ্যতামূলক।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">product_name</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">শুধু invoice বা checkout-এ display করার জন্য; order matching-এর জন্য ব্যবহার হবে না।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">product_type</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">মান হতে হবে <code>"physical_cod"</code>।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">currency</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">মুদ্রার কোড (যেমন: <code>"USD"</code>, <code>"BDT"</code>, <code>"INR"</code>)। <strong>বাধ্যতামূলক:</strong> অর্ডার ভ্যালু ও কুরিয়ার অ্যামাউন্ট সঠিক রাখতে কারেন্সি পাঠানো আবশ্যক। পে-লোডে না পেলে ব্র্যান্ড/গেটওয়ে কনফিগ থেকে ফ্রন্টএন্ডে প্রদর্শিত হবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">product_price</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">পণ্যের মোট মূল্য (যেমন: ১২০০ ৳)।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">courier_charge</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">ডেলিভারি চার্জ (যেমন: ১২০ ৳)।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">amount</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">বাধ্যতামূলক (Must)</span></td>
                      <td className="p-3 text-slate-300">মান হবে <code>0</code> (যেহেতু কোনো গেটওয়ে পেমেন্ট নেই)।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">customer_address</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 text-[10px] font-semibold">ঐচ্ছিক (কুরিয়ার সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">গ্রাহকের পূর্ণ ডেলিভারি ঠিকানা। ক্লায়েন্ট যদি MecoPay ড্যাশবোর্ডে <strong>অটো কুরিয়ার বুকিং (Auto Courier / 1-Click Booking)</strong> সার্ভিস সক্রিয় রাখেন, তবে কুরিয়ার সিস্টেমে পার্সেল বুক করার জন্য গ্রাহকের সম্পূর্ণ ঠিকানা পাঠানো <strong>আবশ্যক</strong>। কুরিয়ার সার্ভিস ব্যবহার না করলে এটি না পাঠালেও চলবে।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">customer_phone</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 text-[10px] font-semibold">ঐচ্ছিক (সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">কুরিয়ার ডেলিভারিম্যানের সাথে যোগাযোগ এবং SMS/WhatsApp নোটিফিকেশন সার্ভিস অন থাকলে মোবাইল নম্বর পাঠানো <strong>আবশ্যক</strong>। সার্ভিস চালু না থাকলে না পাঠালেও সমস্যা নেই।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">customer_email</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-indigo-500/10 text-indigo-300 text-[10px] font-semibold">ঐচ্ছিক (সার্ভিস নির্ভর)</span></td>
                      <td className="p-3 text-slate-300">গ্রাহককে ডিজিটাল অর্ডার ইনভয়েস ও রসিদ পাঠাতে ইমেইল সার্ভিস অন থাকলে গ্রাহকের ইমেইল পাঠানো <strong>আবশ্যক</strong>।</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-emerald-300">customer_name</td>
                      <td className="p-3"><span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px]">ঐচ্ছিক (Optional)</span></td>
                      <td className="p-3 text-slate-300">পার্সেল রিসিভারের নাম (কুরিয়ার শিপমেন্ট পার্সেল ও ইনভয়েসের জন্য)।</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Service Policy Notice Box for Full COD */}
              <div className="bg-slate-950/80 border border-slate-800/80 rounded-2xl p-4 text-xs space-y-2">
                <div className="flex items-center gap-2 font-bold text-emerald-400">
                  <Info className="w-4 h-4 shrink-0" />
                  <span>সার্ভিস ইন্টিগ্রেশন নোট (100% COD / Just Form):</span>
                </div>
                <p className="text-slate-400 leading-relaxed text-[11px]">
                  ১০০% ক্যাশ অন ডেলিভারি (Just Form) অর্ডারের ক্ষেত্রে গ্রাহক ক্লায়েন্টের ওয়েবসাইটে ইতিমধ্যে নাম, ফোন ও ঠিকানা ইনপুট দিয়ে থাকেন। আপনি যদি MecoPay ড্যাশবোর্ডের মাধ্যমে <strong>অটো কুরিয়ার বুকিং</strong> করতে চান, তবে কুরিয়ার পার্সেল বুকিং নিশ্চিত করতে গ্রাহকের সম্পূর্ণ ঠিকানা ও মোবাইল নম্বর পাঠানো আবশ্যক। একইভাবে <strong>SMS, WhatsApp বা Email নোটিফিকেশন</strong> সক্রিয় থাকলে সংশ্লিষ্ট নম্বর ও ইমেইল পাঠাবেন। সার্ভিসগুলো বন্ধ থাকলে এগুলোর কোনোটিই বাধ্যতামূলক নয়।
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: BACKEND INTEGRATION CODE (LARAVEL, PHP, NODE.JS, JS, PYTHON) */}
      {activeTab === 'laravel' && (
        <div className="space-y-6">
          <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <FileCode2 className="w-5 h-5 text-emerald-400" /> যেকোনো কাস্টম ওয়েবসাইট ও ব্যাকএন্ড ইন্টিগ্রেশন কোড
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                আপনার পছন্দের প্রোগ্রামিং ল্যাঙ্গুয়েজ বা ফ্রেমওয়ার্ক নির্বাচন করুন (Laravel, Raw PHP, Node.js, Python):
              </p>
            </div>

            {/* End-to-End Product Lifecycle & Currency Flow Explainer */}
            <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-5 space-y-4">
              <div className="flex items-center gap-2 text-sm font-bold text-emerald-400">
                <Info className="w-4 h-4 shrink-0" />
                <span>ক্লায়েন্টের সাইটে প্রোডাক্ট কারেন্সি ও টাইপ কিভাবে কাজ করে? (Architecture Flow)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3.5 space-y-1.5">
                  <div className="font-bold text-amber-400 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-[10px]">১</span>
                    স্টোরের ডাটাবেজ
                  </div>
                  <p className="text-slate-400 leading-relaxed text-[11px]">
                    মার্চেন্টের সাইটে প্রোডাক্ট টেবিলে <code>product_type</code> (digital, physical_advance, physical_full_payment, physical_cod) এবং <code>currency</code> (BDT, USD, EUR) সেট থাকে।
                  </p>
                </div>
                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3.5 space-y-1.5">
                  <div className="font-bold text-blue-400 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center text-[10px]">২</span>
                    ফ্রন্টএন্ড ফর্ম / Buy Now
                  </div>
                  <p className="text-slate-400 leading-relaxed text-[11px]">
                    গ্রাহক যখন সাইটে কোনো প্রোডাক্টে "অর্ডার করুন" চাপেন, ফ্রন্টএন্ড থেকে প্রোডাক্টের প্রয়োজনীয় তথ্য বা কার্ট ডেটা আপনার ব্যাকএন্ড কন্ট্রোলারে যায়; MecoPay payment payload-এ কেবল client-এর <code>order_id</code> পাঠানো বাধ্যতামূলক।
                  </p>
                </div>
                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3.5 space-y-1.5">
                  <div className="font-bold text-purple-400 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-300 flex items-center justify-center text-[10px]">৩</span>
                    ব্যাকএন্ড কন্ট্রোলার
                  </div>
                  <p className="text-slate-400 leading-relaxed text-[11px]">
                    কন্ট্রোলার প্রোডাক্টের ধরণ ও কারেন্সি রিড করে স্বয়ংক্রিয়ভাবে MecoPay API Worker এর <code>/v1/create-checkout</code> এ পে-লোড পাঠিয়ে <code>checkout_url</code> পায়।
                  </p>
                </div>
                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3.5 space-y-1.5">
                  <div className="font-bold text-emerald-400 flex items-center gap-1.5">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center text-[10px]">৪</span>
                    MecoPay চেকআউট
                  </div>
                  <p className="text-slate-400 leading-relaxed text-[11px]">
                    পে-লোডে BDT থাকলে চেকআউটে <strong>৳</strong> ও বিকাশ/নগদ/কার্ড আসে; USD থাকলে <strong>$</strong> ও ইন্টারন্যাশনাল কার্ড আসে; আর কারেন্সি না দিলে গেটওয়ে সেটিং থেকে অটো শো হয়!
                  </p>
                </div>
              </div>
            </div>

            {/* 500+ Old Products & Mixed Catalog Auto-Detection Guide */}
            <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950/40 border border-indigo-500/30 rounded-2xl p-5 sm:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                    <Layers className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                      ৫০+ বা ৫০০+ পুরোনো প্রোডাক্ট ও মিক্সড ক্যাটালগ অটো-ডিটেকশন গাইড
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold">Zero-Edit Solution</span>
                    </h4>
                    <p className="text-[11px] text-slate-400">
                      আপনার সাইটে ১-২ বছর আগে তৈরি করা ৫০০টি পণ্য থাকলে এবং ডাটাবেজে কোনো <code>product_type</code> কলাম না থাকলেও কিভাবে প্লাগিন বা কন্ট্রোলার প্রতিটি পণ্য স্বয়ংক্রিয়ভাবে চিনে নেবে?
                    </p>
                  </div>
                </div>
              </div>

              {/* 4 Detection Strategies */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                {/* Method 1: WooCommerce */}
                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-4 space-y-2">
                  <div className="font-bold text-emerald-400 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center text-[10px]">১</span>
                      WooCommerce প্লাগিন (১০০% অটোমেটিক)
                    </span>
                    <span className="text-[10px] bg-emerald-500/10 text-emerald-300 px-2 py-0.5 rounded">নো কোডিং</span>
                  </div>
                  <p className="text-slate-300 text-[11px] leading-relaxed">
                    WooCommerce-এর নেটিভ মেথড <code>is_virtual()</code>, <code>is_downloadable()</code> ও <code>needs_shipping()</code> দেখে প্লাগিন নিজে থেকেই বুঝে ফেলে। ক্লায়েন্টকে ১টি পুরোনো প্রডাক্টেও হাত দিতে হবে না।
                  </p>
                </div>

                {/* Method 2: Custom Site Category */}
                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-4 space-y-2">
                  <div className="font-bold text-blue-400 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center text-[10px]">২</span>
                      কাস্টম সাইট: ক্যাটাগরি আইডি দিয়ে অটো-ডিটেকশন
                    </span>
                    <span className="text-[10px] bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded">সুপারিশকৃত</span>
                  </div>
                  <p className="text-slate-300 text-[11px] leading-relaxed">
                    কন্ট্রোলারে মাত্র ২ লাইনে ক্যাটাগরি আইডি দিয়ে ফিল্টার করুন:
                  </p>
                  <pre className="bg-slate-950 p-2 rounded-lg text-[10px] font-mono text-cyan-300 overflow-x-auto">
                    {'$isDigital = in_array($product->category_id, [3, 7]); // ৩=কোর্স, ৭=সফটওয়্যার\n$productType = $isDigital ? "digital" : (($adv > 0) ? "physical_advance" : "physical_cod");'}
                  </pre>
                </div>

                {/* Method 3: Feature & Download Link */}
                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-4 space-y-2">
                  <div className="font-bold text-amber-400 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-[10px]">৩</span>
                      কাস্টম সাইট: ডাউনলোড লিংক বা ফাইল ফিল্ড দেখে
                    </span>
                    <span className="text-[10px] bg-amber-500/10 text-amber-300 px-2 py-0.5 rounded">বৈশিষ্ট্যভিত্তিক</span>
                  </div>
                  <p className="text-slate-300 text-[11px] leading-relaxed">
                    ডিজিটাল পণ্যে কোনো ফাইল বা গুগল ড্রাইভ লিংক থাকে যা ফিজিক্যালে থাকে না:
                  </p>
                  <pre className="bg-slate-950 p-2 rounded-lg text-[10px] font-mono text-amber-300 overflow-x-auto">
                    {'$productType = !empty($product->download_link) ? "digital" : "physical_advance";'}
                  </pre>
                </div>

                {/* Method 4: 1-Second SQL Bulk Update */}
                <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-4 space-y-2">
                  <div className="font-bold text-purple-400 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-full bg-purple-500/20 text-purple-300 flex items-center justify-center text-[10px]">৪</span>
                      ডাটাবেজে ১ সেকেন্ডের বাল্ক SQL কমান্ড
                    </span>
                    <span className="text-[10px] bg-purple-500/10 text-purple-300 px-2 py-0.5 rounded">পার্মানেন্ট</span>
                  </div>
                  <p className="text-slate-300 text-[11px] leading-relaxed">
                    ডাটাবেজে ১ সেকেন্ডে ৫০০ প্রোডাক্ট আপডেট করার কুয়েরি:
                  </p>
                  <pre className="bg-slate-950 p-2 rounded-lg text-[10px] font-mono text-purple-300 overflow-x-auto">
                    {'ALTER TABLE products ADD COLUMN product_type VARCHAR(20) DEFAULT "physical_advance";\nUPDATE products SET product_type = "digital" WHERE category_id IN (3, 7);'}
                  </pre>
                </div>
              </div>

              {/* 3 Important Architectural Rules */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-2">
                <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-3 space-y-1">
                  <div className="text-[11px] font-bold text-emerald-400 flex items-center gap-1">
                    <Check className="w-3.5 h-3.5 shrink-0" /> ফ্রি ডেলিভারি (Free Shipping)
                  </div>
                  <p className="text-[10px] text-slate-400">
                    শিপিং ফি ০ টাকা হলে <code>courier_charge: 0</code> পাঠাবেন। সিস্টেমে <strong>"Free (৳0)"</strong> শো করবে, কোনো এরর হবে না।
                  </p>
                </div>

                <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-3 space-y-1">
                  <div className="text-[11px] font-bold text-cyan-400 flex items-center gap-1">
                    <Check className="w-3.5 h-3.5 shrink-0" /> ডাইনামিক কারেন্সি (Multi-Currency)
                  </div>
                  <p className="text-[10px] text-slate-400">
                    স্টোরের যেকোনো কারেন্সি (<code>BDT</code>, <code>USD</code>, <code>EUR</code>) পাঠাতে পারবেন। না পাঠালে গেটওয়ে থেকে অটো শো হবে।
                  </p>
                </div>

                <div className="bg-slate-950/70 border border-slate-800/80 rounded-xl p-3 space-y-1">
                  <div className="text-[11px] font-bold text-amber-400 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /> মিক্সড কার্ট প্রটেকশন
                  </div>
                  <p className="text-[10px] text-slate-400">
                    একই অর্ডারে ডিজিটাল + ফিজিক্যাল প্রোডাক্ট অর্ডার করা ব্লক করা হয় ক্রেতার ফুল ডিজিটাল চুরি ঠেকাতে।
                  </p>
                </div>
              </div>
            </div>

            {/* Language & Framework Switcher */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button
                onClick={() => setFrameworkTab('laravel')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                  frameworkTab === 'laravel'
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Laravel (PHP)
              </button>
              <button
                onClick={() => setFrameworkTab('php')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                  frameworkTab === 'php'
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Raw PHP (cURL)
              </button>
              <button
                onClick={() => setFrameworkTab('nodejs')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                  frameworkTab === 'nodejs'
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Node.js (Express / Axios)
              </button>
              <button
                onClick={() => setFrameworkTab('python')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                  frameworkTab === 'python'
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                Python (Django / Flask / Requests)
              </button>
            </div>

            {/* STEP 1: API Setup */}
            <div className="space-y-2">
              <h4 className="text-sm font-bold text-white flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-mono">1</span>
                ধাপ ১: API Endpoint ও Headers
              </h4>
              <pre className="bg-slate-950 p-4 rounded-2xl border border-slate-800 text-xs font-mono text-emerald-300">
{`ENDPOINT: POST https://api.mecopay.space/v1/create-checkout
HEADERS:
  Authorization: Bearer <YOUR_BRAND_API_KEY>
  Content-Type: application/json`}
              </pre>
              <p className="text-[11px] text-slate-400">
                * কোনো Brand ID আলাদা পাস করার দরকার নেই—API Key থেকেই MecoPay Server Engine সংশ্লিষ্ট ব্র্যান্ড শনাক্ত করে নেবে।
              </p>
              <div className="bg-blue-950/30 border border-blue-500/30 rounded-2xl p-4 space-y-2 text-xs text-slate-300">
                <h4 className="font-bold text-blue-300">Payment-create Controller Credential Setup</h4>
                <p>Dashboard-এর <strong>Brand Settings → API Key</strong> থেকে API Key নিন। এটি শুধু আপনার server-side payment-create controller-এ রাখবেন:</p>
                <pre className="bg-slate-950 p-3 rounded-xl text-blue-200 overflow-x-auto">{`MECOPAY_API_KEY=আপনার-brand-api-key
Authorization: Bearer &lt;MECOPAY_API_KEY&gt;`}</pre>
                <p className="text-slate-400">API Key কখনো browser/frontend, public JSON বা GitHub-এ রাখবেন না। Payment-create request-এ আলাদা webhook secret পাঠানোর দরকার নেই।</p>
              </div>
              {frameworkTab === 'nodejs' && (
                <div className="bg-slate-950/80 border border-cyan-500/30 rounded-2xl p-4 space-y-2 text-xs text-slate-300">
                  <h4 className="font-bold text-cyan-300">Node.js Dependency ও Database Setup</h4>
                  <p>Client backend-এর project directory থেকে dependency install করুন:</p>
                  <pre className="bg-slate-950 p-3 rounded-xl text-cyan-200 overflow-x-auto">{'npm install express axios pg'}</pre>
                  <p><code>db.query(...)</code> client-এর নিজের database adapter। PostgreSQL হলে <code>../db</code> module-এ pool/client export করে controller-এ <code>const db = require('../db')</code> যোগ করুন; নিজের <code>orders</code> table এবং <code>mecopay_invoice_no</code> column অনুযায়ী query মিলিয়ে নিন। অন্য database হলে তার official driver ব্যবহার করুন।</p>
                  <pre className="bg-slate-950 p-3 rounded-xl text-cyan-200 overflow-x-auto">{'MECOPAY_API_KEY=আপনার-brand-api-key\nMECOPAY_WEBHOOK_SECRET=Dashboard-এর-generated-webhook-secret'}</pre>
                </div>
              )}
              {frameworkTab === 'python' && (
                <div className="bg-slate-950/80 border border-emerald-500/30 rounded-2xl p-4 space-y-2 text-xs text-slate-300">
                  <h4 className="font-bold text-emerald-300">Python Dependency ও Request Wrapper Setup</h4>
                  <p>দেওয়া sample-টি Django ORM ভিত্তিক। Django project-এর virtual environment-এ install করুন:</p>
                  <pre className="bg-slate-950 p-3 rounded-xl text-emerald-200 overflow-x-auto">{'pip install requests django\n# requirements.txt: requests>=2.31, Django>=4.2'}</pre>
                  <p>কোডটি নিজের Django view/service function-এর ভিতরে বসিয়ে request body থেকে <code>request_data</code> dictionary তৈরি করুন। <code>Order</code> model, <code>mecopay_invoice_no</code> column এবং URL routing client-এর project অনুযায়ী মিলিয়ে নিন। Flask ব্যবহার করলে <code>requests</code> রাখুন, কিন্তু Django ORM-এর বদলে নিজের Flask database adapter ও route wrapper ব্যবহার করুন।</p>
                  <pre className="bg-slate-950 p-3 rounded-xl text-emerald-200 overflow-x-auto">{'MECOPAY_API_KEY=আপনার-brand-api-key\nMECOPAY_WEBHOOK_SECRET=Dashboard-এর-generated-webhook-secret'}</pre>
                </div>
              )}
            </div>

            {/* STEP 2: Code Implementation */}
            <div className="space-y-3 pt-2">
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-mono">2</span>
                  ধাপ ২: <code>{getFrameworkFileName()}</code> ফাইলে কোড বসান
                </h4>
                <button
                  onClick={() => handleCopy(getFrameworkCode(), 4)}
                  className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-semibold"
                >
                  {copiedIndex === 4 ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copy Code
                </button>
              </div>

              <pre className="bg-slate-950 p-4 sm:p-5 rounded-2xl border border-slate-800 text-xs font-mono text-slate-200 overflow-x-auto leading-relaxed max-h-[500px]">
{getFrameworkCode()}
              </pre>

              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-1.5 text-xs text-slate-300">
                <h5 className="font-bold text-white">কোডটি কীভাবে কাজ করে?</h5>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>কাস্টমার যখন চেকআউট বাটনে চাপ দেয়, কোড রিকোয়েস্টটি ধরে।</li>
                  <li>বাধ্যতামূলক <code>order_id</code>; response-এর <code>invoice_no</code> নিজের order row-তে সংরক্ষণ করুন; এবং ঐচ্ছিক display field <code>product_name</code> ও <code>courier_charge</code> সহ পে-লোড বানায়।</li>
                  <li>MecoPay Worker-এর <code>https://api.mecopay.space/v1/create-checkout</code> এ পাঠায়।</li>
                  <li>MecoPay সাথে সাথে একটি সিকিউর <code>checkout_url</code> রিটার্ন করে এবং কাস্টমার রিডাইরেক্ট হয় (অথবা ফুল COD-তে অর্ডার কনফার্ম করে)।</li>
                  <li>MecoPay সাথে সাথে <code>invoice_no</code> এবং payment-required হলে <code>checkout_url</code> রিটার্ন করে; controller-কে <code>invoice_no</code> নিজের order table-এ সংরক্ষণ করতে হবে।</li>
                </ol>
                <div className="bg-slate-900/80 border border-emerald-500/20 rounded-xl p-3 space-y-2">
                  <p className="font-semibold text-emerald-300">Payment-create Response Contract</p>
                  <pre className="bg-slate-950 p-3 rounded-xl text-emerald-200 overflow-x-auto">{`{
  "success": true,
  "invoice_no": "MecoPay-invoice-no",
  "checkout_url": "https://www.mecopay.space/pay?...",
  "requires_payment": true,
  "product_type": "physical_full_payment",
  "product_price": 1250,
  "courier_charge": 120,
  "payable_amount": 1370,
  "cod_due": 0
}`}</pre>
                  <p className="text-slate-400">Payment-create controller এই response থেকে <code>invoice_no</code> save করবে এবং <code>checkout_url</code>-এ customer-কে redirect করবে। Full COD হলে <code>requires_payment: false</code> থাকবে এবং checkout_url নাও থাকতে পারে।</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: WEBHOOKS & AUTO-SYNC */}
      {activeTab === 'webhook' && (
        <div className="space-y-6">
          <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Webhook className="w-5 h-5 text-emerald-400" /> Webhook Handling & Automatic Order Update
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                পেমেন্ট সফল হলে বা কুরিয়ার পার্সেল ডেলিভারি কনফার্ম করলে MecoPay স্বয়ংক্রিয়ভাবে আপনার ব্যাকএন্ডে একটি সিকিউর POST Webhook পাঠাবে।
              </p>
            </div>

            <div className="bg-amber-950/30 border border-amber-500/30 rounded-2xl p-4 space-y-2 text-xs">
              <h4 className="font-bold text-amber-300">Webhook Secret ও Signature সেটআপ</h4>
              <p className="text-slate-300">Secret কখনো frontend, JSON payload বা GitHub-এ রাখবেন না। এটি merchant backend-এর environment secret হিসেবে রাখুন এবং একই secret দিয়ে raw request body-এর HMAC-SHA256 hash যাচাই করুন।</p>
              <pre className="bg-slate-950 p-3 rounded-xl text-amber-200 overflow-x-auto">{`MECOPAY_WEBHOOK_SECRET=আপনার-দীর্ঘ-র‌্যান্ডম-secret
Header: X-MecoPay-Signature: <hex HMAC-SHA256(raw request body)>
Laravel: .env + config cache
Node.js: process.env.MECOPAY_WEBHOOK_SECRET
Python: os.environ["MECOPAY_WEBHOOK_SECRET"]
Raw PHP: getenv("MECOPAY_WEBHOOK_SECRET")`}</pre>
              <p className="text-slate-400">Signature যাচাই সফল না হওয়া পর্যন্ত order status, payment status বা digital link update করবেন না।</p>
              <div className="bg-slate-950/70 border border-amber-500/20 rounded-xl p-3 space-y-1.5">
                <p className="font-semibold text-amber-200">Success/Webhook Controller Setup:</p>
                <ol className="list-decimal pl-5 space-y-1 text-slate-300">
                  <li>Dashboard → <strong>Webhooks</strong> → আপনার brand নির্বাচন করে client backend-এর webhook URL দিন।</li>
                  <li>Dashboard যে generated webhook secret দেখাবে, সেটি copy করুন; নতুন secret নিজে বানানোর প্রয়োজন নেই।</li>
                  <li>Client backend-এ শুধু environment secret হিসেবে বসান: <code>MECOPAY_WEBHOOK_SECRET=...</code>।</li>
                  <li>Laravel/Raw PHP/Node/Python controller raw request body দিয়ে <code>X-MecoPay-Signature</code> যাচাই করবে।</li>
                </ol>
                <p className="text-slate-400">Payment-create controller-এ <code>MECOPAY_API_KEY</code> লাগে; success controller-এ <code>MECOPAY_WEBHOOK_SECRET</code> লাগে—দুটি আলাদা credential।</p>
                <p className="text-slate-400">Webhook status contract: Digital ও Physical Full Payment-এ <code>status: paid</code>, Physical Advance-এ <code>status: advance_paid</code>, আর Full COD courier event-এ <code>status: cod_confirmed</code> থাকবে।</p>
              </div>
            </div>

            {/* Webhook JSON Payload */}
            <div className="space-y-4">
              <span className="text-xs font-bold text-slate-300">Webhook POST Payload Samples (ক্যাটাগরি অনুযায়ী আলাদা পে-লোড):</span>
              
              {/* Product 1: Digital Webhook */}
              <div className="bg-slate-950 p-4 rounded-2xl border border-blue-500/30 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-blue-400">১. ডিজিটাল প্রোডাক্টের Webhook Payload (কোর্স / ইবুক):</span>
                  <span className="text-[10px] bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded font-mono">product_type: digital</span>
                </div>
                <pre className="text-xs font-mono text-blue-200 overflow-x-auto">
{`{
  "event": "payment.completed",
  "event_time": "2026-10-18T10:00:00.000Z",
  "data": {
    "invoice_no": "8f2b1c9a-0000-4000-8000-9812000000",
    "order_id": "ORD-9812",
    "product_name": "Fullstack AI Mastery Course",
    "product_type": "digital",
    "currency": "USD",
    "product_price": 1500,
    "total_price": 1500,
    "payable_amount": 1500,
    "cod_due": 0,
    "status": "paid",
    "delivery": {
      "telegram_link": "https://t.me/+join_onetime_secret",
      "facebook_group_link": "https://facebook.com/groups/aimasteryvip",
      "facebook_access_code": "MCP-8921",
      "google_drive_link": "https://drive.google.com/drive/folders/sample_materials"
    },
    "courier": null
  }
}`}
                </pre>
              </div>

              {/* Product 2: Physical Advance Webhook */}
              <div className="bg-slate-950 p-4 rounded-2xl border border-amber-500/30 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-amber-400">২. ফিজিক্যাল প্রোডাক্ট (Advance + COD) Webhook Payload:</span>
                  <span className="text-[10px] bg-amber-500/10 text-amber-300 px-2 py-0.5 rounded font-mono">product_type: physical_advance</span>
                </div>
                <pre className="text-xs font-mono text-amber-200 overflow-x-auto">
{`{
  "event": "payment.completed",
  "event_time": "2026-10-18T10:00:00.000Z",
  "data": {
    "invoice_no": "8f2b1c9a-0000-4000-8000-7734000000",
    "order_id": "ORD-7734",
    "product_name": "Wireless Noise Cancelling Headphones",
    "product_type": "physical_advance",
    "currency": "USD",
    "product_price": 1250,
    "courier_charge": 120,
    "total_price": 1370,
    "payable_amount": 150,
    "cod_due": 1220,
    "status": "advance_paid",
    "courier": {
      "provider": "steadfast",
      "tracking_code": "STDF-883108",
      "tracking_url": "https://steadfast.com.bd/tracking/STDF-883108"
    }
  }
}`}
                </pre>
              </div>

              {/* Product 3: Physical 100% COD Webhook */}
              <div className="bg-slate-950 p-4 rounded-2xl border border-emerald-500/30 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-emerald-400">৩. ফিজিক্যাল প্রোডাক্ট (১০০% Full COD / Just Form) Webhook Payload:</span>
                  <span className="text-[10px] bg-emerald-500/10 text-emerald-300 px-2 py-0.5 rounded font-mono">product_type: physical_cod</span>
                </div>
                <pre className="text-xs font-mono text-emerald-200 overflow-x-auto">
{`{
  "event": "order.courier_booked",
  "event_time": "2026-10-18T10:00:00.000Z",
  "data": {
    "invoice_no": "8f2b1c9a-0000-4000-8000-5519000000",
    "order_id": "ORD-5519",
    "product_name": "Leather Executive Wallet",
    "product_type": "physical_cod",
    "currency": "USD",
    "product_price": 1200,
    "courier_charge": 120,
    "total_price": 1320,
    "payable_amount": 0,
    "cod_due": 1320,
    "status": "cod_confirmed",
    "courier": {
      "provider": "steadfast",
      "tracking_code": "STDF-883109",
      "tracking_url": "https://steadfast.com.bd/tracking/STDF-883109"
    }
  }
}`}
                </pre>
              </div>

              {/* Product 4: Physical Full Payment Webhook */}
              <div className="bg-slate-950 p-4 rounded-2xl border border-cyan-500/30 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-cyan-400">৪. ফিজিক্যাল প্রোডাক্ট (Full Payment) Webhook Payload:</span>
                  <span className="text-[10px] bg-cyan-500/10 text-cyan-300 px-2 py-0.5 rounded font-mono">product_type: physical_full_payment</span>
                </div>
                <pre className="text-xs font-mono text-cyan-200 overflow-x-auto">
{`{
  "event": "payment.completed",
  "event_time": "2026-10-18T10:00:00.000Z",
  "data": {
    "invoice_no": "8f2b1c9a-0000-4000-8000-8840000000",
    "order_id": "ORD-8840",
    "product_name": "Premium Physical Product",
    "product_type": "physical_full_payment",
    "currency": "USD",
    "product_price": 1250,
    "courier_charge": 120,
    "total_price": 1370,
    "payable_amount": 1370,
    "cod_due": 0,
    "status": "paid",
    "courier": {
      "provider": "steadfast",
      "tracking_code": "STDF-883110",
      "tracking_url": "https://steadfast.com.bd/tracking/STDF-883110"
    }
  }
}`}
                </pre>
                <p className="text-[11px] text-slate-400">Physical full payment-এর ক্ষেত্রেও courier object পাঠানো হবে; courier booking সম্পন্ন হলে provider, tracking_code এবং tracking_url-এর বাস্তব value বসবে।</p>
              </div>
            </div>

            {/* Multi-language Webhook Controller Implementation */}
            <div className="space-y-4 pt-3 border-t border-slate-800">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-mono">
                      ✓
                    </span>
                    ধাপ ২: <code>{getWebhookFileName()}</code> ফাইলে Webhook হ্যান্ডলার বসান
                  </h4>
                  <p className="text-xs text-slate-400 mt-0.5">
                    MecoPay Worker ও Edge Function থেকে আসা সিকিউর Webhook ভেরিফাই ও লোকাল ডাটাবেজে অর্ডার আপডেট কোড।
                  </p>
                </div>
                <button
                  onClick={() => handleCopy(getWebhookCode(), 5)}
                  className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-semibold shrink-0"
                >
                  {copiedIndex === 5 ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copy Webhook Code
                </button>
              </div>

              {/* Language Switcher for Webhook */}
              <div className="flex gap-2 overflow-x-auto pb-1">
                <button
                  onClick={() => setWebhookFrameworkTab('laravel')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                    webhookFrameworkTab === 'laravel'
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  Laravel (Artisan)
                </button>
                <button
                  onClick={() => setWebhookFrameworkTab('php')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                    webhookFrameworkTab === 'php'
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  Raw PHP (Core PDO)
                </button>
                <button
                  onClick={() => setWebhookFrameworkTab('nodejs')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                    webhookFrameworkTab === 'nodejs'
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  Node.js (Express)
                </button>
                <button
                  onClick={() => setWebhookFrameworkTab('python')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                    webhookFrameworkTab === 'python'
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  Python (Django / Flask)
                </button>
              </div>

              <pre className="bg-slate-950 p-4 sm:p-5 rounded-2xl border border-slate-800 text-xs font-mono text-slate-200 overflow-x-auto leading-relaxed max-h-[500px]">
{getWebhookCode()}
              </pre>

              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-1.5 text-xs text-slate-300">
                <h5 className="font-bold text-white">Webhook কন্ট্রোলার যেভাবে কাজ করে:</h5>
                <ol className="list-decimal pl-5 space-y-1">
                  <li><strong>HMAC-SHA256 সিগনেচার ভেরিফিকেশন:</strong> <code>X-MecoPay-Signature</code> হেডারের মাধ্যমে প্রেরিত ডাটার নিরাপত্তা ও বৈধতা নিশ্চিত করে।</li>
                  <li><strong>অর্ডার ম্যাপিং:</strong> webhook-এর <code>invoice_no</code> দিয়ে আপনার লোকাল ডাটাবেজে অর্ডার খুঁজে বের করুন। <code>invoice_no</code> হলো MecoPay-এর canonical invoice number; <code>order_id</code> থাকলে অতিরিক্ত মিল হিসেবে ব্যবহার করুন, কিন্তু order_id না থাকলেও invoice_no দিয়ে verification সম্পন্ন হবে।</li>
                  <li><strong>ইভেন্ট হ্যান্ডলিং:</strong> <code>payment.completed</code> হলে ডিজিটাল (পাসকোড প্রদান) বা ফিজিক্যাল (অ্যাডভান্স কনফার্ম ও শিপমেন্ট প্রস্তুত) আপডেট করে। <code>order.courier_booked</code> হলে কুরিয়ার ট্র্যাকিং কোড সংরক্ষণ করে।</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: CLOUDFLARE WORKER CODE (FINAL) */}
      {/* TAB 4: API CREDENTIALS & BASE URL */}
      
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-5">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-semibold border border-emerald-500/20 mb-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Authentication & Single Central Base URL
              </div>
              <h3 className="text-lg font-bold text-white">
                MecoPay Production API Endpoint
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed mt-1">
                ডেভেলপারদের জন্য সিস্টেমে একটি মাত্র সেন্ট্রাল এন্ডপয়েন্ট রয়েছে। আপনার ব্যাকএন্ড/কন্ট্রোলার থেকে এই একক URL-এ POST রিকোয়েস্ট পাঠাবেন, MecoPay Server Engine বাকি সব প্রোডাক্ট টাইপ ও রাউটিং হ্যান্ডেল করবে।
              </p>
            </div>

            <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-3">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold block">Production Base URL</span>
              <div className="font-mono text-sm sm:text-base font-bold text-emerald-400 bg-slate-900/90 p-4 rounded-xl border border-emerald-500/30 select-all flex items-center justify-between">
                <span>https://api.mecopay.space/v1/create-checkout</span>
                <button
                  onClick={() => handleCopy('https://api.mecopay.space/v1/create-checkout', 99)}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs rounded-lg flex items-center gap-1 font-sans transition-all border border-slate-700"
                >
                  {copiedIndex === 99 ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />} Copy URL
                </button>
              </div>
            </div>

            <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-2">
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-bold block">Required Headers</span>
              <pre className="font-mono text-xs text-slate-300 bg-slate-900/90 p-4 rounded-xl border border-slate-800 overflow-x-auto">
{`Content-Type: application/json
Authorization: Bearer <YOUR_BRAND_API_KEY>`}
              </pre>
              <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
                * ব্র্যান্ডের API Key পেতে ড্যাশবোর্ডের মেনু থেকে <strong>Brand</strong> সেকশনে যান। কোনো Brand ID আলাদা পাস করতে হবে না, API Key থেকে MecoPay Server Engine অটো ব্র্যান্ড ডিটেক্ট করবে।
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------- CODE PAYLOADS -----------------

const digitalJsonPayload = `{
  "order_id": "ORD-9812",
  "product_name": "Fullstack AI Mastery Course",
  "product_type": "digital",
  "currency": "USD",
  "product_price": 1500,
  "success_url": "https://yourwebsite.com/payment/callback",
  "cancel_url": "https://yourwebsite.com/payment/cancel",
  "customer_name": "Rahim Ahmed",
  "customer_phone": "01712345678",
  "customer_email": "rahim@example.com",
  "telegram_link": "https://t.me/+joinchat_secretinvite",
  "facebook_group_link": "https://facebook.com/groups/aimasteryvip",
  "google_drive_link": "https://drive.google.com/drive/folders/sample_materials"
}`;

const advanceJsonPayload = `{
  "order_id": "ORD-7734",
  "product_name": "Wireless Noise Cancelling Headphones",
  "product_type": "physical_advance",
  "currency": "USD",
  "product_price": 1250,
  "courier_charge": 120,
  "advance_amount": 150,
  "success_url": "https://yourwebsite.com/payment/callback",
  "cancel_url": "https://yourwebsite.com/payment/cancel",
  "customer_name": "Tanvir Hasan",
  "customer_phone": "01812345678",
  "customer_email": "tanvir@example.com",
  "customer_address": "House 12, Road 4, Sector 7, Uttara, Dhaka"
}`;

const fullPaymentJsonPayload = `{
  "order_id": "ORD-8840",
  "product_name": "Premium Physical Product",
  "product_type": "physical_full_payment",
  "currency": "USD",
  "product_price": 1250,
  "courier_charge": 120,
  "success_url": "https://yourwebsite.com/order/success",
  "cancel_url": "https://yourwebsite.com/checkout",
  "customer_name": "Karim Hasan",
  "customer_phone": "01700000000",
  "customer_email": "karim@example.com",
  "customer_address": "Dhaka, Bangladesh"
}`;

const codJsonPayload = `{
  "order_id": "ORD-5519",
  "product_name": "Leather Executive Wallet",
  "product_type": "physical_cod",
  "currency": "USD",
  "product_price": 1200,
  "courier_charge": 120,
  "success_url": "https://yourwebsite.com/order/success",
  "cancel_url": "https://yourwebsite.com/checkout",
  "customer_name": "Mohammad Ali",
  "customer_phone": "01912345678",
  "customer_email": "ali@example.com",
  "customer_address": "Chawkbazar, Chittagong"
}`;

// 1. Laravel Controller (PHP) with 3 Architecture Guards
const laravelFullController = `<?php

namespace App\\Http\\Controllers;

use Illuminate\\Http\\Request;
use Illuminate\\Support\\Facades\\Http;
use Illuminate\\Support\\Facades\\Cache;
use App\\Models\\Order;

class PaymentController extends Controller
{
    /**
     * MecoPay চেকআউট ইনিশিয়ালাইজ করুন (3-Level Guard Architecture সহ)
     */
    public function initiatePayment(Request $request)
    {
        // -------------------------------------------------------------------------
        // গার্ড ২: কার্ট পলিসি ও মিক্সড প্রোডাক্ট গার্ড (Cart Integrity Rules)
        // -------------------------------------------------------------------------
        $cartItems = $request->input('cart_items', []); // e.g. [['id' => 1, 'type' => 'digital'], ...]
        if (!empty($cartItems)) {
            $digitalCount = 0;
            $physicalCount = 0;

            foreach ($cartItems as $item) {
                if (($item['type'] ?? '') === 'digital') {
                    $digitalCount++;
                } else {
                    $physicalCount++;
                }
            }

            // রুল ১: মিক্সড কার্ট সম্পূর্ণ নিষিদ্ধ (ডিজিটাল + ফিজিক্যাল একসাথে নয়)
            if ($digitalCount > 0 && $physicalCount > 0) {
                return back()->with('error', 'কার্ট পলিসি এরর: ডিজিটাল এবং ফিজিক্যাল প্রোডাক্ট একই সাথে এক কার্টে কেনা নিষিদ্ধ। অনুগ্রহ করে আলাদা অর্ডার করুন।');
            }

            // রুল ২: ডিজিটাল প্রোডাক্ট এক অর্ডারে সর্বোচ্চ ১টি নেওয়া যাবে
            if ($digitalCount > 1) {
                return back()->with('error', 'কার্ট পলিসি এরর: ডিজিটাল প্রোডাক্টের ক্ষেত্রে এক অর্ডারে সর্বোচ্চ ১টি প্রোডাক্ট নেওয়া যাবে।');
            }
        }

        // -------------------------------------------------------------------------
        // গার্ড ৩: ৪ ধরনের প্রোডাক্টের স্মার্ট অটো-ডিটেকশন ও পে-লোড প্রস্তুতি
        // -------------------------------------------------------------------------
        // ৫০০+ পুরোনো প্রোডাক্টের ডাটাবেজ আপডেট ছাড়াই ক্যাটাগরি বা ফাইল দেখে অটো ডিটেকশন:
        // $isDigital = (!empty($product->download_link) || in_array($product->category_id, [1, 5]));
        // $detectedType = $isDigital ? 'digital' : (($advanceAmount > 0) ? 'physical_advance' : 'physical_cod');
        $productType = strtolower(trim((string) $request->input('product_type', '')));
        $allowedTypes = ['digital', 'physical_advance', 'physical_full_payment', 'physical_cod'];
        if (!in_array($productType, $allowedTypes, true)) {
            return back()->with('error', 'Unsupported product_type.');
        }
        $orderId = $request->input('order_id');
        $productName = $request->input('product_name');

        if (empty($orderId)) {
            return back()->with('error', 'order_id আবশ্যক।');
        }

        $productPrice = (float) $request->input('product_price', 0);
        // কুরিয়ার চার্জ ডাইনামিকালি রিকোয়েস্ট বা প্রোডাক্ট থেকে নিন (ফ্রি ডেলিভারি হলে ০ থাকবে):
        $courierCharge = (float) $request->input('courier_charge', 0);
        // আপনার ডাটাবেজ বা রিকোয়েস্ট থেকে কারেন্সি নিন (যেমন: BDT, USD, EUR ইত্যাদি):
        $currency = strtoupper(trim($request->input('currency', 'BDT')));

        $payload = [
            'order_id'         => $orderId,
            'product_name'     => $productName ?: 'General Item',
            'product_type'     => $productType,
            'currency'         => $currency,
            'customer_name'    => $request->input('customer_name', 'Guest Customer'),
            'customer_phone'   => $request->input('customer_phone', ''),
            'customer_email'   => $request->input('customer_email', ''),
            'customer_address' => $request->input('customer_address', ''),
            'success_url'     => route('payment.success'),
            'cancel_url'       => route('payment.cancel'),
        ];

        if ($productType === 'digital') {
            // ডিজিটাল: ১০০% পেমেন্ট ও রিটার্ন URL আবশ্যক
            if ($productPrice <= 0) {
                return back()->with('error', 'ডিজিটাল প্রোডাক্টের জন্য product_price আবশ্যক।');
            }
            $payload['product_price'] = $productPrice;
            $payload['telegram_link'] = $request->input('telegram_link', 'https://t.me/+secret_link');
            $payload['facebook_group_link'] = $request->input('facebook_group_link');
            $payload['google_drive_link'] = $request->input('google_drive_link');
        } elseif ($productType === 'physical_advance') {
            // ফিজিক্যাল অ্যাডভান্স: টোটাল এমাউন্ট, কুরিয়ার চার্জ এবং অ্যাডভান্স ফি
            if ($productPrice <= 0) {
                return back()->with('error', 'ফিজিক্যাল প্রোডাক্টের জন্য product_price আবশ্যক।');
            }
            // ক্লায়েন্টের প্রোডাক্ট/স্টোর থেকে ডাইনামিক অগ্রিম ফি; total-এর সমান হলে due COD শূন্য হবে:
            $advanceAmount = (float) $request->input('advance_amount', 0);
            $payload['product_price'] = $productPrice;
            $payload['courier_charge'] = $courierCharge;
            $payload['advance_amount'] = $advanceAmount;
        } elseif ($productType === 'physical_full_payment') {
            $payload['product_price'] = $productPrice;
            $payload['courier_charge'] = $courierCharge;
        } else { // physical_cod
            // ১০০% ক্যাশ অন ডেলিভারি: গেটওয়ে চার্জ ০ এবং বাকি টাকা কুরিয়ার ডিউ
            $payload['product_price'] = $productPrice;
            $payload['courier_charge'] = $courierCharge;
        }

        // MecoPay Worker Endpoint এ একক সেন্ট্রাল POST Call
        $response = Http::withHeaders([
            'Authorization' => 'Bearer ' . env('MECOPAY_API_KEY'),
            'Content-Type'  => 'application/json',
        ])->post('https://api.mecopay.space/v1/create-checkout', $payload);

        if ($response->successful()) {
            $data = $response->json();
            $invoiceNo = $data['invoice_no'] ?? null;
            if ($invoiceNo) {
                // নিজের order row-তে invoice_no সংরক্ষণ করুন; পরের webhook-এ invoice_no থাকবে, order_id থাকলে অতিরিক্ত মিল হবে।
                Order::where('id', $orderId)->orWhere('order_id', $orderId)->update(['mecopay_invoice_no' => $invoiceNo]);
            }
            if (!empty($data['checkout_url'])) {
                return redirect()->away($data['checkout_url']);
            }
            if (isset($data['requires_payment']) && $data['requires_payment'] === false) {
                return redirect()->route('order.success')->with('success', 'ক্যাশ অন ডেলিভারি অর্ডার সফলভাবে কনফার্ম হয়েছে!');
            }
        }

        return back()->with('error', 'Checkout error: ' . ($response->json('error') ?? 'MecoPay সার্ভারের সাথে সংযোগে ব্যর্থ হয়েছে।'));
    }
}`;

// 2. Raw PHP (Core PHP) with 3 Architecture Guards
const rawPhpController = `<?php
/**
 * Raw PHP Integration for MecoPay (With cart rules and 4-product validation)
 */

$apiKey = "YOUR_MECOPAY_API_KEY_HERE";
$endpoint = "https://api.mecopay.space/v1/create-checkout";

// -------------------------------------------------------------------------
// গার্ড ২: কার্ট পলিসি ও মিক্সড প্রোডাক্ট গার্ড (Cart Integrity Rules)
// -------------------------------------------------------------------------
$cart = $_SESSION['cart'] ?? [];
if (!empty($cart)) {
    $digitalCount = 0;
    $physicalCount = 0;
    foreach ($cart as $item) {
        if (($item['type'] ?? '') === 'digital') {
            $digitalCount++;
        } else {
            $physicalCount++;
        }
    }
    // রুল ১: মিক্সড কার্ট সম্পূর্ণ নিষিদ্ধ
    if ($digitalCount > 0 && $physicalCount > 0) {
        die("কার্ট পলিসি এরর: একই সাথে ডিজিটাল ও ফিজিক্যাল প্রোডাক্ট কেনা যাবে না।");
    }
    // রুল ২: ডিজিটাল সর্বোচ্চ ১টি
    if ($digitalCount > 1) {
        die("কার্ট পলিসি এরর: এক অর্ডারে সর্বোচ্চ ১টি ডিজিটাল প্রোডাক্ট নেওয়া যাবে।");
    }
}

// -------------------------------------------------------------------------
// গার্ড ৩: ৪ ধরনের প্রোডাক্ট স্মার্ট অটো-ডিটেকশন ও পে-লোড প্রস্তুতি
// -------------------------------------------------------------------------
// ৫০০+ পুরোনো প্রোডাক্টের ডাটাবেজ আপডেট ছাড়াই ক্যাটাগরি বা ফাইল দেখে অটো ডিটেকশন:
// $isDigital = (!empty($product['download_link']) || in_array($product['category_id'] ?? 0, [3, 7]));
// $detectedType = $isDigital ? 'digital' : (($advance > 0) ? 'physical_advance' : 'physical_cod');
$productType = strtolower(trim((string) ($_POST['product_type'] ?? '')));
$allowedTypes = ['digital', 'physical_advance', 'physical_full_payment', 'physical_cod'];
if (!in_array($productType, $allowedTypes, true)) {
    die('Unsupported product_type.');
}
$orderId = $_POST['order_id'] ?? null;
$productName = $_POST['product_name'] ?? "E-Commerce Product";

if (empty($orderId)) {
    die("order_id আবশ্যক।");
}

// ডাইনামিক কারেন্সি (BDT, USD, EUR ইত্যাদি):
$currency = strtoupper(trim($_POST['currency'] ?? 'BDT'));

// প্রোডাক্টের আসল মূল্য ও কুরিয়ার চার্জ ফর্ম বা ডাটাবেজ থেকে ডাইনামিকালি নিন (ফ্রি ডেলিভারি হলে ০):
$productPrice = (float) ($_POST['product_price'] ?? 0);
$courierCharge = (float) ($_POST['courier_charge'] ?? 0);

$payload = [
    "order_id"         => $orderId,
    "product_name"     => $productName,
    "product_type"     => $productType,
    "currency"         => $currency,
    "customer_name"    => $_POST['customer_name'] ?? "Customer Name",
    "customer_phone"   => $_POST['customer_phone'] ?? "",
    "customer_email"   => $_POST['customer_email'] ?? "",
    "customer_address" => $_POST['customer_address'] ?? "",
    // সাইটের নিজস্ব ডোমেন ও ডাইনামিক রিটার্ন পাথ:
    "success_url"     => ((!empty($_SERVER['HTTPS']) && $_SERVER['HTTPS'] !== 'off') ? "https://" : "http://") . $_SERVER['HTTP_HOST'] . "/payment_success.php",
    "cancel_url"       => ((!empty($_SERVER['HTTPS']) && $_SERVER['HTTPS'] !== 'off') ? "https://" : "http://") . $_SERVER['HTTP_HOST'] . "/cart.php"
];

if ($productType === 'digital') {
    if ($productPrice <= 0) die("ডিজিটাল পণ্যের product_price আবশ্যক।");
    $payload['product_price'] = $productPrice;
    $payload['telegram_link'] = "https://t.me/+join_chat";
    $payload['facebook_group_link'] = $_POST['facebook_group_link'] ?? null;
    $payload['google_drive_link'] = $_POST['google_drive_link'] ?? null;
} elseif ($productType === 'physical_advance') {
    $advance = (float) ($_POST['advance_amount'] ?? 0); // total-এর সমান হলে physical full payment
    $payload['product_price'] = $productPrice;
    $payload['courier_charge'] = $courierCharge;
    $payload['advance_amount'] = $advance;
} elseif ($productType === 'physical_full_payment') {
    $payload['product_price'] = $productPrice;
    $payload['courier_charge'] = $courierCharge;
} else { // physical_cod
    $payload['product_price'] = $productPrice;
    $payload['courier_charge'] = $courierCharge;
}

// cURL রিকোয়েস্ট একক এন্ডপয়েন্টে
$ch = curl_init($endpoint);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer " . $apiKey,
    "Content-Type: application/json"
]);

$response = curl_exec($ch);
$httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

$result = json_decode($response, true);
$invoiceNo = $result['invoice_no'] ?? null;
if ($httpCode === 200 && $invoiceNo) {
    // $pdo হলো আপনার বিদ্যমান merchant database PDO connection।
    $saveInvoice = $pdo->prepare(
        'UPDATE orders SET mecopay_invoice_no = :invoice_no WHERE id = :order_id OR order_id = :order_id'
    );
    $saveInvoice->execute([
        ':invoice_no' => $invoiceNo,
        ':order_id' => $orderId,
    ]);
}
// invoice_no-ই পরের webhook verification-এর canonical identifier; order_id থাকলে অতিরিক্ত মিল হবে।

if ($httpCode === 200 && !empty($result['checkout_url'])) {
    header("Location: " . $result['checkout_url']);
    exit;
} elseif ($httpCode === 200 && (($result['requires_payment'] ?? true) === false)) {
    header('Location: ' . $payload['success_url']);
    exit;
} else {
    echo "MecoPay Error: " . ($result['error'] ?? 'Connection Failed');
}
?>`;

// 3. Node.js (Express / Axios) with 3 Architecture Guards
const nodeJsFullController = `const express = require('express');
const axios = require('axios');
const app = express();

app.use(express.json());
app.use(express.urlencoded({ extended: true }));

const MECOPAY_API_KEY = process.env.MECOPAY_API_KEY || 'your_api_key_here';
const MECOPAY_ENDPOINT = 'https://api.mecopay.space/v1/create-checkout';

app.post('/api/checkout', async (req, res) => {
  try {
    const {
      cart_items = [],
      product_type,
      order_id,
      product_name = 'Item',
      product_price = null,
      courier_charge = 0,
      advance_amount = 0,
      currency = 'BDT',
      customer_name = 'Guest Customer',
      customer_phone = '',
      customer_email = '',
      customer_address = '',
      success_url,
      cancel_url
    } = req.body;
    const allowedTypes = ['digital', 'physical_advance', 'physical_full_payment', 'physical_cod'];
    const finalProductType = String(product_type || '').toLowerCase().trim();
    if (!allowedTypes.includes(finalProductType)) {
      return res.status(400).json({ error: 'Unsupported product_type.' });
    }
    const priceValue = Number(product_price ?? 0);
    const courierChargeValue = Number(courier_charge || 0);
    const advanceAmountValue = Number(advance_amount || 0);

    // -------------------------------------------------------------------------
    // গার্ড ২: কার্ট পলিসি ও মিক্সড প্রোডাক্ট গার্ড (Cart Integrity Rules)
    // -------------------------------------------------------------------------
    if (cart_items.length > 0) {
      const digitalCount = cart_items.filter(i => i.type === 'digital').length;
      const physicalCount = cart_items.filter(i => i.type !== 'digital').length;

      if (digitalCount > 0 && physicalCount > 0) {
        return res.status(400).json({ error: 'কার্ট পলিসি এরর: ডিজিটাল ও ফিজিক্যাল পণ্য একসাথে এক অর্ডারে কেনা যাবে না।' });
      }
      if (digitalCount > 1) {
        return res.status(400).json({ error: 'কার্ট পলিসি এরর: ডিজিটাল প্রোডাক্ট এক অর্ডারে সর্বোচ্চ ১টি নেওয়া যাবে।' });
      }
    }

    // -------------------------------------------------------------------------
    // গার্ড ৩: ৪ ধরনের প্রোডাক্ট স্মার্ট অটো-ডিটেকশন ও পে-লোড প্রস্তুতি
    // -------------------------------------------------------------------------
    // ৫০০+ পুরোনো পণ্যের ডাটাবেজ আপডেট ছাড়াই ক্যাটাগরি বা ফাইল দেখে অটো ডিটেকশন:
    // const isDigital = Boolean(product?.download_link || [3, 7].includes(product?.category_id));
    // const detectedType = isDigital ? 'digital' : (advance_amount > 0 ? 'physical_advance' : 'physical_cod');
    const finalCurrency = String(currency || 'BDT').toUpperCase();

    if (!order_id) {
      return res.status(400).json({ error: 'order_id ফিল্ড আবশ্যক।' });
    }

    const payload = {
      order_id,
      product_name,
      product_type: finalProductType,
      currency: finalCurrency,
      customer_name,
      customer_phone,
      customer_email,
      customer_address,
      success_url: success_url || (req.protocol + '://' + req.get('host') + '/payment/success'),
      cancel_url: cancel_url || (req.protocol + '://' + req.get('host') + '/cart')
    };

    if (finalProductType === 'digital') {
      if (priceValue <= 0) return res.status(400).json({ error: 'ডিজিটাল প্রোডাক্টের জন্য product_price আবশ্যক।' });
      payload.product_price = priceValue;
      payload.telegram_link = 'https://t.me/+join_group';
      payload.facebook_group_link = req.body.facebook_group_link || null;
      payload.google_drive_link = req.body.google_drive_link || null;
    } else if (finalProductType === 'physical_advance') {
      if (priceValue <= 0) return res.status(400).json({ error: 'ফিজিক্যাল প্রোডাক্টের জন্য product_price আবশ্যক।' });
      payload.product_price = priceValue;
      payload.courier_charge = courierChargeValue;
      payload.advance_amount = advanceAmountValue;
    } else if (finalProductType === 'physical_full_payment') {
      payload.product_price = priceValue;
      payload.courier_charge = courierChargeValue;
    } else { // physical_cod
      payload.product_price = priceValue;
      payload.courier_charge = courierChargeValue;
    }

    const response = await axios.post(MECOPAY_ENDPOINT, payload, {
      headers: {
        Authorization: \`Bearer \${MECOPAY_API_KEY}\`,
        'Content-Type': 'application/json'
      }
    });

    const invoiceNo = response.data.invoice_no || null;
    if (invoiceNo) {
      // db হলো আপনার বিদ্যমান PostgreSQL/MySQL client বা query adapter।
      await db.query(
        'UPDATE orders SET mecopay_invoice_no = $1 WHERE id = $2 OR order_id = $2',
        [invoiceNo, order_id]
      );
    }
    // invoice_no-ই পরের webhook verification-এর canonical identifier; order_id থাকলে অতিরিক্ত মিল হবে。

    if (response.data.checkout_url) {
      return res.redirect(response.data.checkout_url);
    }

    return res.json({ success: true, message: 'COD Order booked', data: response.data });
  } catch (error) {
    return res.status(500).json({ error: error.response?.data?.error || error.message });
  }
});`;

// 4. Python (Django / Flask / Requests) with 3 Architecture Guards
const pythonFullController = `import requests
from django.db.models import Q
from orders.models import Order

MECOPAY_API_KEY = "your_brand_api_key_here"
MECOPAY_ENDPOINT = "https://api.mecopay.space/v1/create-checkout"

    # -------------------------------------------------------------------------
    # গার্ড ২: কার্ট পলিসি ও মিক্সড প্রোডাক্ট গার্ড (Cart Integrity Rules)
    # -------------------------------------------------------------------------
    cart_items = request_data.get('cart_items', [])
    if cart_items:
        digital_count = sum(1 for item in cart_items if item.get('type') == 'digital')
        physical_count = sum(1 for item in cart_items if item.get('type') != 'digital')

        if digital_count > 0 and physical_count > 0:
            return {"error": "কার্ট পলিসি এরর: একই সাথে ডিজিটাল ও ফিজিক্যাল প্রোডাক্ট কেনা নিষিদ্ধ।"}
        if digital_count > 1:
            return {"error": "কার্ট পলিসি এরর: ডিজিটাল প্রোডাক্ট এক অর্ডারে সর্বোচ্চ ১টি নেওয়া যাবে।"}

    # -------------------------------------------------------------------------
    # গার্ড ৩: ৪ ধরনের প্রোডাক্ট স্মার্ট অটো-ডিটেকশন ও পে-লোড প্রস্তুতি
    # -------------------------------------------------------------------------
    # ৫০০+ পুরোনো পণ্যের ক্যাটাগরি বা ফাইল দেখে অটো ডিটেকশন:
    # is_digital = bool(product.get("download_link") or product.get("category_id") in [3, 7])
    # detected_type = "digital" if is_digital else ("physical_advance" if advance_amount > 0 else "physical_cod")
    product_type = str(request_data.get('product_type') or '').lower().strip()
    allowed_types = {'digital', 'physical_advance', 'physical_full_payment', 'physical_cod'}
    if product_type not in allowed_types:
        return {"error": "Unsupported product_type."}
    if not request_data.get('order_id'):
        return {"error": "order_id আবশ্যক।"}
    product_price = float(request_data.get('product_price') or 0)
    courier_charge = float(request_data.get('courier_charge') or 0) # ফ্রি ডেলিভারি হলে ০ থাকবে

    payload = {
        "order_id": request_data['order_id'],
        "product_name": request_data.get('product_name', "Digital E-Book"),
        "product_type": product_type,
        "currency": (request_data.get('currency') or "BDT").upper(),
        "customer_name": request_data.get('customer_name', "Guest"),
        "customer_phone": request_data.get('customer_phone', ""),
        "customer_email": request_data.get('customer_email', ""),
        "customer_address": request_data.get('customer_address', "Dhaka"),
        # সাইটের ডাইনামিক ডোমেন বা রিকোয়েস্ট URL:
        "success_url": request_data.get('success_url') or "/payment/callback",
        "cancel_url": request_data.get('cancel_url') or "/cart"
    }

    if product_type == 'digital':
        if product_price <= 0:
            return {"error": "ডিজিটাল প্রোডাক্টের জন্য price ফিল্ড আবশ্যক।"}
        payload["product_price"] = product_price
        payload["telegram_link"] = "https://t.me/+vip_invite"
        payload["facebook_group_link"] = request_data.get("facebook_group_link")
        payload["google_drive_link"] = request_data.get("google_drive_link")
    elif product_type == 'physical_advance':
        advance = float(request_data.get('advance_amount') or 0)
        payload["product_price"] = product_price
        payload["courier_charge"] = courier_charge
        payload["advance_amount"] = advance
    elif product_type == 'physical_full_payment':
        payload["product_price"] = product_price
        payload["courier_charge"] = courier_charge
    else: # physical_cod
        payload["product_price"] = product_price
        payload["courier_charge"] = courier_charge

    headers = {
        "Authorization": f"Bearer {MECOPAY_API_KEY}",
        "Content-Type": "application/json"
    }

    response = requests.post(MECOPAY_ENDPOINT, json=payload, headers=headers)
    result = response.json()
    invoice_no = result.get('invoice_no')
    if response.status_code == 200 and invoice_no:
        # আপনার Django Order model/ORM বা বিদ্যমান DB adapter ব্যবহার করুন।
        Order.objects.filter(Q(pk=request_data['order_id']) | Q(order_id=request_data['order_id'])).update(mecopay_invoice_no=invoice_no)
    # invoice_no-ই পরের webhook verification-এর canonical identifier; order_id থাকলে অতিরিক্ত মিল হবে।

    if response.status_code == 200:
        if result.get("checkout_url"):
            return {"redirect": result["checkout_url"]}
        return {"success": True, "order_id": request_data['order_id'], "requires_payment": result.get("requires_payment", True)}
    else:
        return {"error": result.get("error", "API request failed")}
`;

const laravelWebhookCode = `<?php

namespace App\\Http\\Controllers;

use Illuminate\\Http\\Request;
use App\\Models\\Order;

class MecoPayWebhookController extends Controller
{
    /**
     * MecoPay Webhook Listener & Order Fulfillment
     * Synchronized with Cloudflare API Worker & Supabase Edge Functions
     */
    public function handle(Request $request)
    {
        $rawPayload = $request->getContent();
        $receivedSignature = $request->header('X-MecoPay-Signature');
        $webhookSecret = env('MECOPAY_WEBHOOK_SECRET');

        // -------------------------------------------------------------------------
        // ১. সিকিউরিটি গার্ড: HMAC-SHA256 সিগনেচার ভেরিফিকেশন
        // -------------------------------------------------------------------------
        if (empty($webhookSecret) || $webhookSecret === 'your_webhook_secret_here' || empty($receivedSignature)) {
            return response()->json(['error' => 'Webhook signature configuration or header is missing.'], 401);
        }
        $expectedSignature = hash_hmac('sha256', $rawPayload, $webhookSecret);
        if (!hash_equals($expectedSignature, (string)$receivedSignature)) {
            return response()->json(['error' => 'Invalid webhook signature.'], 401);
        }

        $payload = $request->all();
        $event = $payload['event'] ?? '';
        $data = $payload['data'] ?? $payload;

        // -------------------------------------------------------------------------
        // ২. canonical invoice_no এবং optional order_id দিয়ে অর্ডার অনুসন্ধান
        // -------------------------------------------------------------------------
        $orderId = $data['order_id'] ?? $payload['order_id'] ?? null;
        $invoiceNo = $data['invoice_no'] ?? $payload['invoice_no'] ?? null;

        if (!$orderId && !$invoiceNo) {
            return response()->json(['error' => 'Missing order_id or invoice_no in webhook.'], 400);
        }

        // নিজের database-এ আগে সংরক্ষিত order_id ও invoice_no দুটোই মিলতে হবে।
        $orderQuery = Order::query();
        if ($orderId) {
            $orderQuery->where(function ($query) use ($orderId) {
                $query->where('id', $orderId)->orWhere('order_id', $orderId);
            });
        }
        if ($invoiceNo) {
            $orderQuery->where('mecopay_invoice_no', $invoiceNo);
        }
        $order = $orderQuery->first();

        if (!$order) {
            return response()->json(['error' => 'Order not found in merchant database.'], 404);
        }

        // -------------------------------------------------------------------------
        // ৩. ইভেন্ট ১: ডিজিটাল বা ফিজিক্যাল অ্যাডভান্স পেমেন্ট সফল হওয়া
        // -------------------------------------------------------------------------
        if ($event === 'payment.completed') {
            $productType = $data['order_type'] ?? $data['product_type'] ?? $order->product_type ?? 'digital';
            $paidAmount = $data['payable_amount'] ?? $data['amount_paid'] ?? $data['amount'] ?? 0;

            if ($productType === 'digital') {
                // ডিজিটাল প্রোডাক্ট: ১০০% পেমেন্ট সফল, অ্যাক্সেস ও ডেলিভারি লিংক সংরক্ষণ
                $delivery = is_array($data['delivery'] ?? null) ? $data['delivery'] : [];
                $digitalPasscode = $delivery['facebook_access_code'] ?? $delivery['passcode'] ?? null;
                $telegramLink    = $delivery['telegram_link'] ?? null;
                $fbGroupLink     = $delivery['facebook_group_link'] ?? null;
                $driveLink       = $delivery['google_drive_link'] ?? null;

                $order->update([
                    'payment_status' => 'paid',
                    'order_status'   => 'completed',
                    'amount_paid'    => $paidAmount,
                    'access_passcode'=> $digitalPasscode,
                    'telegram_link'  => $telegramLink,
                    'facebook_group' => $fbGroupLink,
                    'google_drive'   => $driveLink,
                ]);
            } else {
                // ফিজিক্যাল প্রোডাক্ট (অ্যাডভান্স পেইড): অগ্রিম কনফার্ম, বাকি টাকা কুরিয়ারে ডিউ ও ট্র্যাকিং
                $trackingCode = $data['courier']['tracking_code'] ?? $data['courier_tracking_code'] ?? null;
                $trackingUrl  = $data['courier']['tracking_url'] ?? $data['tracking_url'] ?? null;
                $provider     = $data['courier']['provider'] ?? $data['courier_provider'] ?? 'steadfast';

                $isFullPayment = ($productType === 'physical_full_payment');
                $order->update([
                    'payment_status'   => $isFullPayment ? 'paid' : 'advance_paid',
                    'order_status'     => 'ready_for_dispatch',
                    'advance_amount'   => $isFullPayment ? 0 : $paidAmount,
                    'courier_due'      => $isFullPayment ? 0 : ($data['cod_due'] ?? $order->cod_due),
                    'courier_provider' => $provider,
                    'courier_tracking' => $trackingCode,
                    'tracking_url'     => $trackingUrl,
                ]);
            }

            if ($productType === 'digital') {
                return response()->json([
                    'status' => 'success',
                    'message' => 'Digital payment processed and access links granted.',
                    'order_id' => $orderId,
                    'product_type' => 'digital',
                    'passcode' => $digitalPasscode,
                    'telegram_link' => $telegramLink,
                    'facebook_group_link' => $fbGroupLink,
                    'google_drive_link' => $driveLink,
                    'digital_access' => [
                        'passcode' => $digitalPasscode,
                        'telegram_link' => $telegramLink,
                        'facebook_group_link' => $fbGroupLink,
                        'google_drive_link' => $driveLink,
                    ]
                ], 200);
            } else {
                return response()->json([
                    'status' => 'success',
                    'message' => $isFullPayment ? 'Physical full payment processed and shipment prepared.' : 'Physical advance payment recorded and courier tracking initialized.',
                    'order_id' => $orderId,
                    'product_type' => $productType,
                    'advance_amount' => $paidAmount,
                    'courier_due' => $isFullPayment ? 0 : ($data['cod_due'] ?? $order->cod_due),
                    'courier_provider' => $provider,
                    'tracking_code' => $trackingCode,
                    'tracking_url' => $trackingUrl,
                    'courier' => [
                        'provider' => $provider,
                        'tracking_code' => $trackingCode,
                        'tracking_url' => $trackingUrl,
                    ]
                ], 200);
            }
        }

        // -------------------------------------------------------------------------
        // ৪. ইভেন্ট ২: ১০০% Full COD অর্ডারে কুরিয়ার বুকিং নিশ্চিত হওয়া
        // -------------------------------------------------------------------------
        if ($event === 'order.courier_booked') {
            $courier = $data['courier'] ?? [];
            $trackingCode = $courier['tracking_code'] ?? ($data['courier_tracking_code'] ?? null);
            $provider = $courier['provider'] ?? 'steadfast';
            $trackingUrl = $courier['tracking_url'] ?? null;

            $order->update([
                'order_status'     => 'shipped',
                'courier_provider' => $provider,
                'courier_tracking' => $trackingCode,
                'tracking_url'     => $trackingUrl,
            ]);

            return response()->json([
                'status' => 'success',
                'message' => 'Courier booking recorded with parcel tracking URL.',
                'order_id' => $orderId,
                'courier_provider' => $provider,
                'tracking_code' => $trackingCode,
                'tracking_url' => $trackingUrl,
                'courier' => [
                    'provider' => $provider,
                    'tracking_code' => $trackingCode,
                    'tracking_url' => $trackingUrl,
                ]
            ], 200);
        }

        return response()->json(['status' => 'ignored', 'message' => 'Event not handled.'], 200);
    }
}
`;

const rawPhpWebhookCode = `<?php
/**
 * MecoPay Webhook Listener & Order Fulfillment (Core PHP / PDO)
 * Synchronized with Cloudflare API Worker & Supabase Edge Functions
 */

header('Content-Type: application/json');

$webhookSecret = getenv('MECOPAY_WEBHOOK_SECRET');
$rawPayload = file_get_contents('php://input');
$receivedSignature = $_SERVER['HTTP_X_MECOPAY_SIGNATURE'] ?? '';

// -------------------------------------------------------------------------
// ১. সিকিউরিটি গার্ড: HMAC-SHA256 সিগনেচার ভেরিফিকেশন
// -------------------------------------------------------------------------
if (empty($webhookSecret) || $webhookSecret === 'your_webhook_secret_here' || empty($receivedSignature)) {
    http_response_code(401);
    echo json_encode(['error' => 'Webhook signature configuration or header is missing.']);
    exit;
}
$expectedSignature = hash_hmac('sha256', $rawPayload, $webhookSecret);
if (!hash_equals($expectedSignature, (string)$receivedSignature)) {
    http_response_code(401);
    echo json_encode(['error' => 'Invalid webhook signature.']);
    exit;
}

$payload = json_decode($rawPayload, true);
if (!$payload) {
    http_response_code(400);
    echo json_encode(['error' => 'Invalid JSON payload.']);
    exit;
}

$event = $payload['event'] ?? '';
$data = $payload['data'] ?? $payload;

// -------------------------------------------------------------------------
// ২. canonical invoice_no এবং optional order_id
// -------------------------------------------------------------------------
$orderId = $data['order_id'] ?? $payload['order_id'] ?? null;
$invoiceNo = $data['invoice_no'] ?? $payload['invoice_no'] ?? null;

if (!$orderId && !$invoiceNo) {
    http_response_code(400);
    echo json_encode(['error' => 'Missing order_id or invoice_no in webhook.']);
    exit;
}

// $pdo আপনার existing merchant bootstrap/database connection থেকে নিন।
// উদাহরণ: require __DIR__ . '/bootstrap.php'; যেখানে $pdo তৈরি হয়েছে。
if (!isset($pdo) || !($pdo instanceof PDO)) {
    http_response_code(500);
    echo json_encode(['error' => 'Merchant PDO connection is not configured.']);
    exit;
}
$conditions = [];
$params = [];
if ($orderId) {
    $conditions[] = '(id = :order_id OR order_id = :order_id)';
    $params[':order_id'] = $orderId;
}
if ($invoiceNo) {
    $conditions[] = 'mecopay_invoice_no = :invoice_no';
    $params[':invoice_no'] = $invoiceNo;
}
$lookup = $pdo->prepare('SELECT * FROM orders WHERE ' . implode(' AND ', $conditions) . ' LIMIT 1');
$lookup->execute($params);
$order = $lookup->fetch();
if (!$order) {
    http_response_code(404);
    echo json_encode(['error' => 'Order not found in merchant database.']);
    exit;
}
$merchantOrderId = $order['id'];

// -------------------------------------------------------------------------
// ৩. ইভেন্ট ১: ডিজিটাল বা ফিজিক্যাল অ্যাডভান্স পেমেন্ট সফল হওয়া
// -------------------------------------------------------------------------
if ($event === 'payment.completed') {
    $productType = $data['order_type'] ?? $data['product_type'] ?? 'digital';
    $paidAmount = (float)($data['payable_amount'] ?? $data['amount_paid'] ?? $data['amount'] ?? 0);
            $delivery = is_array($data['delivery'] ?? null) ? $data['delivery'] : [];
            $passcode = $delivery['facebook_access_code'] ?? $delivery['passcode'] ?? '';
            $telegramLink = $delivery['telegram_link'] ?? '';
            $fbGroupLink = $delivery['facebook_group_link'] ?? '';
            $driveLink = $delivery['google_drive_link'] ?? '';
    $codDue = $data['cod_due'] ?? null;
    $trackingCode = $data['courier']['tracking_code'] ?? $data['courier_tracking_code'] ?? '';
    $trackingUrl = $data['courier']['tracking_url'] ?? $data['tracking_url'] ?? '';
    $provider = $data['courier']['provider'] ?? $data['courier_provider'] ?? 'steadfast';

    if ($productType === 'digital') {
        // ডিজিটাল: ১০০% ফুল পেমেন্ট সফল, পাসকোড ও এক্সেস লিংক কনফার্ম
        $save = $pdo->prepare("UPDATE orders SET payment_status = 'paid', order_status = 'completed', amount_paid = :amount, mecopay_passcode = :passcode, telegram_link = :telegram, facebook_group = :facebook, google_drive = :drive WHERE id = :id");
        $save->execute([':amount' => $paidAmount, ':passcode' => $passcode, ':telegram' => $telegramLink, ':facebook' => $fbGroupLink, ':drive' => $driveLink, ':id' => $merchantOrderId]);

        echo json_encode([
            'status' => 'success',
            'message' => 'Digital payment processed and access links granted.',
            'order_id' => $orderId,
            'product_type' => 'digital',
            'passcode' => $passcode,
            'telegram_link' => $telegramLink,
            'facebook_group_link' => $fbGroupLink,
            'google_drive_link' => $driveLink,
            'digital_access' => [
                'passcode' => $passcode,
                'telegram_link' => $telegramLink,
                'facebook_group_link' => $fbGroupLink,
                'google_drive_link' => $driveLink,
            ]
        ]);
        exit;
    } else {
        // ফিজিক্যাল অ্যাডভান্স: অগ্রিম পেমেন্ট কনফার্ম, পার্সেল শিপমেন্ট ও ট্র্যাকিং প্রস্তুত
        $isFullPayment = ($productType === 'physical_full_payment');
        $save = $pdo->prepare("UPDATE orders SET payment_status = :payment_status, order_status = 'ready_for_dispatch', advance_amount = :amount, courier_due = :cod_due, courier_provider = :provider, courier_tracking = :tracking, tracking_url = :tracking_url WHERE id = :id");
        $save->execute([':payment_status' => $isFullPayment ? 'paid' : 'advance_paid', ':amount' => $isFullPayment ? 0 : $paidAmount, ':cod_due' => $isFullPayment ? 0 : $codDue, ':provider' => $provider, ':tracking' => $trackingCode, ':tracking_url' => $trackingUrl, ':id' => $merchantOrderId]);

        echo json_encode([
            'status' => 'success',
            'message' => $isFullPayment ? 'Physical full payment processed and shipment prepared.' : 'Physical advance payment recorded and courier tracking initialized.',
            'order_id' => $orderId,
            'product_type' => $productType,
            'advance_amount' => $paidAmount,
            'cod_due' => $codDue,
            'courier_provider' => $provider,
            'tracking_code' => $trackingCode,
            'tracking_url' => $trackingUrl,
            'courier' => [
                'provider' => $provider,
                'tracking_code' => $trackingCode,
                'tracking_url' => $trackingUrl,
            ]
        ]);
        exit;
    }
}

// -------------------------------------------------------------------------
// ৪. ইভেন্ট ২: ১০০% Full COD অর্ডারে কুরিয়ার বুকিং নিশ্চিত হওয়া
// -------------------------------------------------------------------------
if ($event === 'order.courier_booked') {
    $save = $pdo->prepare("UPDATE orders SET order_status = 'shipped', courier_provider = :provider, courier_tracking = :tracking, tracking_url = :tracking_url WHERE id = :id");
    $save->execute([':provider' => $data['courier']['provider'] ?? 'steadfast', ':tracking' => $data['courier']['tracking_code'] ?? ($data['courier_tracking_code'] ?? ''), ':tracking_url' => $data['courier']['tracking_url'] ?? ($data['tracking_url'] ?? ''), ':id' => $merchantOrderId]);
    $trackingCode = $data['courier']['tracking_code'] ?? $data['courier_tracking_code'] ?? '';
    $provider = $data['courier']['provider'] ?? 'steadfast';
    $trackingUrl = $data['courier']['tracking_url'] ?? '';


    echo json_encode([
        'status' => 'success',
        'message' => 'Courier tracking updated successfully with parcel tracking URL.',
        'order_id' => $orderId,
        'courier_provider' => $provider,
        'tracking_code' => $trackingCode,
        'tracking_url' => $trackingUrl,
        'courier' => [
            'provider' => $provider,
            'tracking_code' => $trackingCode,
            'tracking_url' => $trackingUrl,
        ]
    ]);
    exit;
}

echo json_encode(['status' => 'ignored']);
?>`;

const nodeJsWebhookCode = `const express = require('express');
const crypto = require('crypto');
const db = require('../db'); // আপনার existing PostgreSQL query client
const app = express();

// গুরুত্বপূর্ণ: সিগনেচার ভেরিফিকেশনের জন্য হুবহু raw body প্রয়োজন
app.use(express.json({
  verify: (req, res, buf) => {
    req.rawBody = buf.toString('utf8');
  }
}));

const WEBHOOK_SECRET = process.env.MECOPAY_WEBHOOK_SECRET;

app.post('/api/mecopay/webhook', async (req, res) => {
  try {
    const receivedSignature = req.headers['x-mecopay-signature'];

    // -------------------------------------------------------------------------
    // ১. সিকিউরিটি গার্ড: HMAC-SHA256 সিগনেচার ভেরিফিকেশন
    // -------------------------------------------------------------------------
    if (!WEBHOOK_SECRET || WEBHOOK_SECRET === 'your_webhook_secret_here' || !receivedSignature) {
      return res.status(401).json({ error: 'Webhook signature configuration or header is missing.' });
    }
    const expectedSignature = crypto
      .createHmac('sha256', WEBHOOK_SECRET)
      .update(req.rawBody || JSON.stringify(req.body))
      .digest('hex');

    if (receivedSignature !== expectedSignature) {
      return res.status(401).json({ error: 'Invalid webhook signature.' });
    }

    const { event, data } = req.body;
    const eventData = data || req.body;

    // -------------------------------------------------------------------------
    // ২. canonical invoice_no এবং optional order_id
    // -------------------------------------------------------------------------
    const orderId = eventData.order_id || req.body.order_id;
    const invoiceNo = eventData.invoice_no || req.body.invoice_no;

    if (!orderId && !invoiceNo) {
      return res.status(400).json({ error: 'Missing order_id or invoice_no in webhook.' });
    }
    const lookupConditions = [];
    const lookupParams = [];
    if (orderId) {
      lookupParams.push(orderId);
      lookupConditions.push('(id::text = $' + lookupParams.length + ' OR order_id = $' + lookupParams.length + ')');
    }
    if (invoiceNo) {
      lookupParams.push(invoiceNo);
      lookupConditions.push('mecopay_invoice_no = $' + lookupParams.length);
    }
    const lookupSql = 'SELECT * FROM orders WHERE ' + lookupConditions.join(' AND ') + ' LIMIT 1';
    const orderResult = await db.query(
      lookupSql,
      lookupParams
    );
    const order = orderResult.rows[0];
    if (!order) return res.status(404).json({ error: 'Order not found in merchant database.' });

    // -------------------------------------------------------------------------
    // ৩. ইভেন্ট ১: ডিজিটাল বা ফিজিক্যাল অ্যাডভান্স পেমেন্ট সফল হওয়া
    // -------------------------------------------------------------------------
    if (event === 'payment.completed') {
      const productType = eventData.order_type || eventData.product_type || 'digital';
      const paidAmount = eventData.payable_amount ?? eventData.amount_paid ?? eventData.amount;

      if (productType === 'digital') {
        // ডিজিটাল: ১০০% পেমেন্ট সফল, পাসকোড ও এক্সেস লিংক প্রদান
        const delivery = eventData.delivery || {};
        const passcode = delivery.facebook_access_code || delivery.passcode || '';
        const telegramLink = delivery.telegram_link || '';
        const fbGroupLink = delivery.facebook_group_link || '';
        const driveLink = delivery.google_drive_link || '';
        await db.query('UPDATE orders SET payment_status = $1, order_status = $2, amount_paid = $3, mecopay_passcode = $4, telegram_link = $5, facebook_group = $6, google_drive = $7 WHERE id = $8', ['paid', 'completed', paidAmount, passcode, telegramLink, fbGroupLink, driveLink, order.id]);

        return res.status(200).json({
          status: 'success',
          message: 'Digital payment processed and access links granted.',
          order_id: orderId,
          product_type: 'digital',
          passcode,
          telegram_link: telegramLink,
          facebook_group_link: fbGroupLink,
          google_drive_link: driveLink,
          digital_access: {
            passcode,
            telegram_link: telegramLink,
            facebook_group_link: fbGroupLink,
            google_drive_link: driveLink
          }
        });
      } else {
        // ফিজিক্যাল অ্যাডভান্স: অগ্রিম পেমেন্ট কনফার্ম, বাকি টাকা ও কুরিয়ার ট্র্যাকিং
        const codDue = eventData.cod_due;
        const trackingCode = eventData.courier?.tracking_code || eventData.courier_tracking_code;
        const trackingUrl = eventData.courier?.tracking_url || eventData.tracking_url;
        const provider = eventData.courier?.provider || eventData.courier_provider || 'steadfast';
        const isFullPayment = productType === 'physical_full_payment';
        await db.query('UPDATE orders SET payment_status = $1, order_status = $2, advance_amount = $3, courier_due = $4, courier_provider = $5, courier_tracking = $6, tracking_url = $7 WHERE id = $8', [isFullPayment ? 'paid' : 'advance_paid', 'ready_for_dispatch', isFullPayment ? 0 : paidAmount, isFullPayment ? 0 : codDue, provider, trackingCode, trackingUrl, order.id]);

        return res.status(200).json({
          status: 'success',
          message: isFullPayment ? 'Physical full payment processed and shipment prepared.' : 'Physical advance payment recorded and courier tracking initialized.',
          order_id: orderId,
          product_type: productType,
          advance_amount: paidAmount,
          cod_due: isFullPayment ? 0 : codDue,
          courier_provider: provider,
          tracking_code: trackingCode,
          tracking_url: trackingUrl,
          courier: {
            provider,
            tracking_code: trackingCode,
            tracking_url: trackingUrl
          }
        });
      }
    }

    // -------------------------------------------------------------------------
    // ৪. ইভেন্ট ২: ১০০% Full COD অর্ডারে কুরিয়ার বুকিং নিশ্চিত হওয়া
    // -------------------------------------------------------------------------
    if (event === 'order.courier_booked') {
      const trackingCode = eventData.courier?.tracking_code || eventData.courier_tracking_code;
      const provider = eventData.courier?.provider || eventData.courier_provider || 'steadfast';
      const trackingUrl = eventData.courier?.tracking_url || eventData.tracking_url;
      await db.query('UPDATE orders SET order_status = $1, courier_provider = $2, courier_tracking = $3, tracking_url = $4 WHERE id = $5', ['shipped', provider, trackingCode, trackingUrl, order.id]);

      return res.status(200).json({
        status: 'success',
        message: 'Courier booking saved with parcel tracking URL.',
        order_id: orderId,
        courier_provider: provider,
        tracking_code: trackingCode,
        tracking_url: trackingUrl,
        courier: {
          provider,
          tracking_code: trackingCode,
          tracking_url: trackingUrl
        }
      });
    }

    return res.status(200).json({ status: 'ignored' });
  } catch (error) {
    console.error('Webhook processing error:', error);
    return res.status(500).json({ error: error.message });
  }
});
`;

const pythonWebhookCode = `import hmac
import hashlib
import json
import os
from django.http import JsonResponse
from django.views.decorators.csrf import csrf_exempt
from django.db.models import Q
from orders.models import Order  # আপনার existing Order model

WEBHOOK_SECRET = os.getenv("MECOPAY_WEBHOOK_SECRET")

@csrf_exempt
def mecopay_webhook_view(request):
    """
    MecoPay Webhook Listener & Order Fulfillment (Django / Flask)
    Synchronized with Cloudflare API Worker & Supabase Edge Functions
    """
    if request.method != "POST":
        return JsonResponse({"error": "Method not allowed. Use POST."}, status=405)

    raw_body = request.body.decode('utf-8')
    received_signature = request.headers.get("X-MecoPay-Signature", "")

    # -------------------------------------------------------------------------
    # ১. সিকিউরিটি গার্ড: HMAC-SHA256 সিগনেচার ভেরিফিকেশন
    # -------------------------------------------------------------------------
    if not WEBHOOK_SECRET or WEBHOOK_SECRET == "your_webhook_secret_here" or not received_signature:
        return JsonResponse({"error": "Webhook signature configuration or header is missing."}, status=401)
    expected_signature = hmac.new(
        WEBHOOK_SECRET.encode('utf-8'),
        raw_body.encode('utf-8'),
        hashlib.sha256
    ).hexdigest()

    if not hmac.compare_digest(expected_signature, received_signature):
        return JsonResponse({"error": "Invalid webhook signature."}, status=401)

    try:
        payload = json.loads(raw_body)
    except json.JSONDecodeError:
        return JsonResponse({"error": "Invalid JSON payload."}, status=400)

    event = payload.get("event")
    data = payload.get("data") or payload

    # -------------------------------------------------------------------------
    # ২. ক্লায়েন্ট order_id ও invoice_no
    # -------------------------------------------------------------------------
    order_id = data.get("order_id") or payload.get("order_id")
    invoice_no = data.get("invoice_no") or payload.get("invoice_no")

    if not order_id and not invoice_no:
        return JsonResponse({"error": "Missing order_id or invoice_no in webhook."}, status=400)
    lookup = Q()
    if order_id:
        lookup &= (Q(id=order_id) | Q(order_id=order_id))
    if invoice_no:
        lookup &= Q(mecopay_invoice_no=invoice_no)
    order = Order.objects.filter(lookup).first()
    if not order:
        return JsonResponse({"error": "Order not found in merchant database."}, status=404)

    # -------------------------------------------------------------------------
    # ৩. ইভেন্ট ১: ডিজিটাল বা ফিজিক্যাল অ্যাডভান্স পেমেন্ট সফল হওয়া
    # -------------------------------------------------------------------------
    if event == "payment.completed":
        product_type = data.get("order_type") or data.get("product_type") or "digital"
        paid_amount = data.get("payable_amount") or data.get("amount_paid") or data.get("amount")

        if product_type == "digital":
            # ডিজিটাল প্রোডাক্ট: ১০০% পেমেন্ট সফল, পাসকোড ও এক্সেস লিংক ডেলিভারি
            delivery = data.get("delivery") or {}
            passcode = delivery.get("facebook_access_code") or delivery.get("passcode")
            telegram_link = delivery.get("telegram_link")
            fb_group_link = delivery.get("facebook_group_link")
            drive_link = delivery.get("google_drive_link")
            Order.objects.filter(pk=order.pk).update(payment_status="paid", order_status="completed", amount_paid=paid_amount, mecopay_passcode=passcode, telegram_link=telegram_link, facebook_group=fb_group_link, google_drive=drive_link)

            return JsonResponse({
                "status": "success",
                "message": "Digital payment processed and access links granted.",
                "order_id": order_id,
                "product_type": "digital",
                "passcode": passcode,
                "telegram_link": telegram_link,
                "facebook_group_link": fb_group_link,
                "google_drive_link": drive_link,
                "digital_access": {
                    "passcode": passcode,
                    "telegram_link": telegram_link,
                    "facebook_group_link": fb_group_link,
                    "google_drive_link": drive_link,
                }
            })
        else:
            # ফিজিক্যাল অ্যাডভান্স: অগ্রিম পেমেন্ট নিশ্চিত, বাকি টাকা COD ও কুরিয়ার ট্র্যাকিং
            cod_due = data.get("cod_due")
            courier = data.get("courier") or {}
            tracking_code = courier.get("tracking_code") or data.get("courier_tracking_code")
            tracking_url = courier.get("tracking_url") or data.get("tracking_url")
            provider = courier.get("provider") or data.get("courier_provider") or "steadfast"
            is_full_payment = product_type == "physical_full_payment"
            Order.objects.filter(pk=order.pk).update(payment_status="paid" if is_full_payment else "advance_paid", order_status="ready_for_dispatch", advance_amount=0 if is_full_payment else paid_amount, cod_due=0 if is_full_payment else cod_due, courier_provider=provider, courier_tracking=tracking_code, tracking_url=tracking_url)

            return JsonResponse({
                "status": "success",
                "message": "Physical full payment processed and shipment prepared." if is_full_payment else "Physical advance payment recorded and courier tracking initialized.",
                "order_id": order_id,
                "product_type": product_type,
                "advance_amount": paid_amount,
                "cod_due": 0 if is_full_payment else cod_due,
                "courier_provider": provider,
                "tracking_code": tracking_code,
                "tracking_url": tracking_url,
                "courier": {
                    "provider": provider,
                    "tracking_code": tracking_code,
                    "tracking_url": tracking_url,
                }
            })

    # -------------------------------------------------------------------------
    # ৪. ইভেন্ট ২: ১০০% Full COD অর্ডারে কুরিয়ার বুকিং নিশ্চিত হওয়া
    # -------------------------------------------------------------------------
    elif event == "order.courier_booked":
        courier = data.get("courier") or {}
        tracking_code = courier.get("tracking_code") or data.get("courier_tracking_code")
        provider = courier.get("provider", "steadfast")
        tracking_url = courier.get("tracking_url") or data.get("tracking_url")
        Order.objects.filter(pk=order.pk).update(order_status="shipped", courier_provider=provider, courier_tracking=tracking_code, tracking_url=tracking_url)

        return JsonResponse({
            "status": "success",
            "message": "Courier booked successfully with parcel tracking URL.",
            "order_id": order_id,
            "courier_provider": provider,
            "tracking_code": tracking_code,
            "tracking_url": tracking_url,
            "courier": {
                "provider": provider,
                "tracking_code": tracking_code,
                "tracking_url": tracking_url,
            }
        })

    return JsonResponse({"status": "ignored"})
`;
